import { GP_ROLE_TYPE_EXTENSION, GP_ROLE_TYPES, COCM_CONSENT_EXTENSION_URL, PATIENT_SUMMARY_EXTENSION_URL, getActivityAssignmentMode, getActivityAssignmentRoleCode, OBSERVATION_TYPES as OBSERVATION_TYPES$1, LEGACY_OBSERVATION_TYPES, getCarePlanCategoryDisplay, COCM_CONSENT_DATE_EXTENSION_URL, CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL, getClinicalImpressionDisplayFromCoding, COMMUNICATION_MEDIUM_OPTIONS, CALL_STATE_SYSTEM, LEGACY_CALL_STATE_SYSTEM, CALL_STATE_OPTIONS, COMMUNICATION_MEDIUM_SYSTEM, COMMUNICATION_TYPE_SYSTEM, LEGACY_COMMUNICATION_CHANNEL_SYSTEM, VALUE_SET, AI_GENERATED_TAG_SYSTEM, AI_GENERATED_TAG_CODES, FLAG_LOCAL_CODE_SYSTEM, HEALTHCARE_SERVICE_CODESYSTEMS, AVAILABILITY_LAST_VERIFIED_EXTENSION_URL, PATIENT_INTAKE_ELIGIBILITY_SYSTEM, INTAKE_BILLING_FLAG_OPTIONS, PATIENT_ENGAGEMENT_STATUS_SYSTEM, PATIENT_ENGAGEMENT_STATUS_OPTIONS, LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM, LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS, PAUSE_FLAG_REASON_EXTENSION_URL, COCM_PLAN_DEFINITION, TRIAGE_PLAN_DEFINITION, INTAKE_PLAN_DEFINITION, getQuestionnaireItems, STANDARD_QUESTIONNAIRES, LIKERT_FREQUENCY_OPTIONS, isValidQuestionnaireScoring, WSAS_WORK_NOT_APPLICABLE_VALUE, QUESTIONNAIRE_URLS as QUESTIONNAIRE_URLS$1, getObservationDisplayByLoincCode, OBSERVATION_RISK_LEVELS, getAllCarePlanActivityOptions, resolveActivityCode as resolveActivityCode$1, TASK_DRAFT_FORM_STATE_EXTENSION_URL, getTaskTypeDisplay as getTaskTypeDisplay$1, getCarePlanActivityDisplay, CARE_PLAN_ACTIVITY_SYSTEM, SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS } from './constants.mjs';
import { formatDate, formatHumanName } from '@medplum/core';
import { getResourceIdFromReference } from './utils.mjs';

/**
 * Check if address has meaningful data
 * Internal helper function
 */ const hasAddressData = (address)=>Boolean(address.text || address.line && address.line.length > 0 && address.line[0] || address.city || address.state || address.postalCode || address.country);
/**
 * Get display name for a patient.
 * When a preferred name (FHIR nickname) exists, formats as "Legal Name (Preferred Name)".
 */ const getPatientDisplayName = (patient)=>{
    const officialName = patient.name?.find((n)=>n.use === "official") || patient.name?.[0];
    if (!officialName) {
        return `Patient ${patient.id}`;
    }
    const parts = [];
    if (officialName.given?.length) {
        parts.push(...officialName.given);
    }
    if (officialName.family) {
        parts.push(officialName.family);
    }
    if (parts.length === 0) {
        return `Patient ${patient.id}`;
    }
    const legalName = parts.join(" ");
    const nickname = patient.name?.find((n)=>n.use === "nickname" && n !== officialName);
    const preferredName = nickname?.given?.[0];
    if (preferredName) {
        return `${legalName} (${preferredName})`;
    }
    return legalName;
};
/**
 * The patient's name for a persisted FHIR reference `display`, or `undefined`
 * when they have no recorded name — `getPatientDisplayName`'s `Patient {id}`
 * placeholder reads as a UI fallback and is worse than no display at all on a
 * stored reference.
 */ const getPatientReferenceDisplay = (patient)=>{
    const display = getPatientDisplayName(patient);
    return display === `Patient ${patient.id}` ? undefined : display;
};
/**
 * Get referring provider (PCP) reference from patient's generalPractitioner array
 *
 * Resolution order:
 * 1. Find entry with "referring-provider" extension (new format)
 * 2. Fallback to [0] ONLY if it has no extension (legacy format)
 * 3. Return null if [0] is explicitly tagged as something else (e.g., LCSW)
 */ const getPatientReferringProvider = (patient)=>{
    if (!patient.generalPractitioner?.length) {
        return null;
    }
    // First: Look for explicitly tagged referring provider
    const typedEntry = patient.generalPractitioner.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === GP_ROLE_TYPES.REFERRING_PROVIDER));
    if (typedEntry) {
        return typedEntry;
    }
    // Fallback: Check [0] for legacy data (no extension = assume PCP)
    const firstEntry = patient.generalPractitioner[0];
    const hasAnyExtension = firstEntry.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION);
    // If [0] has no GP extension, treat it as legacy PCP data
    // If [0] HAS an extension but it's not REFERRING_PROVIDER, don't return it
    if (!hasAnyExtension) {
        return firstEntry;
    }
    return null;
};
/**
 * Returns true when a gp-role-type code represents the Benchmark primary advocate.
 * Supports both current and legacy value codes.
 */ const isBenchmarkPrimaryAdvocateRoleCode = (valueCode)=>valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW;
/**
 * Get Benchmark primary advocate reference from patient's generalPractitioner array.
 * Function name preserved for backward compatibility.
 */ const getPatientBenchmarkLCSW = (patient)=>{
    return patient.generalPractitioner?.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && isBenchmarkPrimaryAdvocateRoleCode(ext.valueCode))) ?? null;
};
/**
 * Get primary advocate display name from patient's generalPractitioner array.
 * Function name preserved for backward compatibility.
 */ const getLCSWDisplayName = (patient)=>{
    const lcswRef = getPatientBenchmarkLCSW(patient);
    return lcswRef?.display ?? null;
};
/**
 * Get PCP (Primary Care Provider) display name from patient's general practitioner reference
 */ const getPCPDisplayName = (patient)=>{
    const reference = getPatientReferringProvider(patient);
    return reference?.display ?? reference?.reference ?? null;
};
/**
 * Get MRN (Medical Record Number) from patient identifiers
 * Returns null if no MRN identifier exists
 */ const getPatientMRN = (patient)=>{
    if (!patient.identifier || patient.identifier.length === 0) {
        return null;
    }
    const mrnIdentifier = patient.identifier.find((identifier)=>identifier.type?.coding?.some((coding)=>coding.code === "MR"));
    if (mrnIdentifier?.value) {
        return {
            system: mrnIdentifier.system || "",
            value: mrnIdentifier.value
        };
    }
    return null;
};
/**
 * Get appropriate icon name for engagement status code
 * Returns icon name as string for use in UI components
 */ const getEngagementIconName = (code)=>{
    switch(code){
        case "activated":
            return "check";
        case "engaged":
            return "check";
        case "initial-referral":
            return "clock";
        case "unsure":
            return "question-mark";
        case "declined":
            return "x";
        case "unable-to-reach":
            return "alert-triangle";
        default:
            return "question-mark";
    }
};
/**
 * Get active phone number from patient telecom array
 * Returns undefined if no active phone exists
 */ const getActivePatientPhone = (patient)=>patient.telecom?.find((telecom)=>telecom.system === "phone" && !telecom.period?.end)?.value;
/**
 * Get active email from patient telecom array
 * Returns undefined if no active email exists
 */ const getActivePatientEmail = (patient)=>patient.telecom?.find((telecom)=>telecom.system === "email" && !telecom.period?.end)?.value;
/**
 * Get active address from patient address array
 * Filters out addresses with 'old' use or ended periods
 * Returns undefined if no active address exists or address has no data
 */ const getActivePatientAddress = (patient)=>{
    const address = patient.address?.find((candidate)=>candidate.use !== "old" && (!candidate.period || !candidate.period.end));
    return address && hasAddressData(address) ? address : undefined;
};
/**
 * Get active telecom entries from patient telecom array
 * Filters out telecom entries with ended periods
 */ const getActivePatientTelecom = (patient)=>patient.telecom?.filter((telecom)=>!telecom.period?.end) || [];
/**
 * Format patient address for display
 * Returns formatted address string or empty string if address has no data
 */ const formatPatientAddress = (address)=>{
    if (!address || !hasAddressData(address)) {
        return "";
    }
    if (address.text) {
        return address.text;
    }
    const parts = [];
    if (address.line?.length && address.line[0]) {
        parts.push(address.line[0]);
    }
    const cityStateZip = [
        address.city,
        address.state,
        address.postalCode
    ].filter(Boolean).join(" ");
    if (cityStateZip) {
        parts.push(cityStateZip);
    }
    return parts.join("\n");
};
/**
 * Get current (active) patient addresses
 * Filters out addresses with 'old' use or ended periods
 * Returns empty array if no active addresses exist
 */ const getCurrentPatientAddresses = (patient)=>patient.address?.filter((addr)=>addr.use !== "old" && (!addr.period || !addr.period.end)) || [];
/**
 * Get managing organization info from patient
 * Returns organization reference with ID and display name
 */ const getPatientManagingOrganization = (patient)=>{
    const orgRef = patient.managingOrganization?.reference;
    if (!orgRef) return null;
    const orgId = orgRef.replace("Organization/", "");
    const orgName = patient.managingOrganization?.display || "Unknown Practice";
    return {
        id: orgId,
        name: orgName
    };
};
/**
 * Extracts CoCM consent (verified flag + date) from the Patient resource's
 * `cocm-consent` complex extension — populated via the patient edit form's
 * "CoCM Consent Verified" checkbox and "CoCM Consent Date" field
 * (`ReferralInformationSection`). This is distinct from the CoCM CarePlan's
 * `cocm-consent-date` extension (captured automatically at enrollment); see
 * `resolveCoCMConsent` in `cocm-eligibility.service` for how the two sources
 * are reconciled for billing/eligibility display.
 * @agentic @benchmark/core
 */ const getCoCMConsentFromPatient = (patient)=>{
    const cocmExtension = patient.extension?.find((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
    if (!cocmExtension?.extension) return undefined;
    const verified = Boolean(cocmExtension.extension.find((sub)=>sub.url === "verified")?.valueBoolean);
    const date = cocmExtension.extension.find((sub)=>sub.url === "date")?.valueDate;
    return {
        verified,
        date
    };
};
/**
 * Extracts patient summary from Patient resource extension.
 * @agentic @benchmark/core
 */ const getPatientSummary = (patient)=>{
    const summaryExtension = patient.extension?.find((ext)=>ext.url === PATIENT_SUMMARY_EXTENSION_URL);
    if (!summaryExtension?.extension) {
        return undefined;
    }
    const textExt = summaryExtension.extension.find((ext)=>ext.url === "text");
    const lastUpdatedExt = summaryExtension.extension.find((ext)=>ext.url === "lastUpdated");
    const authorExt = summaryExtension.extension.find((ext)=>ext.url === "author");
    const generatedByExt = summaryExtension.extension.find((ext)=>ext.url === "generatedBy");
    const modelUsedExt = summaryExtension.extension.find((ext)=>ext.url === "modelUsed");
    const promptVersionExt = summaryExtension.extension.find((ext)=>ext.url === "promptVersion");
    const text = textExt?.valueString;
    const lastUpdated = lastUpdatedExt?.valueDateTime;
    const authorReference = authorExt?.valueReference?.reference;
    const authorDisplay = authorExt?.valueReference?.display;
    const generatedBy = generatedByExt?.valueCode ?? "manual";
    const modelUsed = modelUsedExt?.valueString;
    const promptVersion = promptVersionExt?.valueString;
    if (!text || !lastUpdated || !authorReference || !authorDisplay) {
        return undefined;
    }
    return {
        text,
        lastUpdated,
        authorReference,
        authorDisplay,
        generatedBy,
        modelUsed,
        promptVersion
    };
};
/**
 * Builds patient summary extension for FHIR Patient resource.
 * @agentic @benchmark/core
 */ const buildPatientSummaryExtension = (options)=>{
    const { text, authorReference, generatedBy, modelUsed, promptVersion } = options;
    if (!authorReference.display) {
        throw new Error("Author reference must include display name");
    }
    const extensionItems = [
        {
            url: "text",
            valueString: text
        },
        {
            url: "lastUpdated",
            valueDateTime: new Date().toISOString()
        },
        {
            url: "author",
            valueReference: authorReference
        },
        {
            url: "generatedBy",
            valueCode: generatedBy
        }
    ];
    if (modelUsed) {
        extensionItems.push({
            url: "modelUsed",
            valueString: modelUsed
        });
    }
    if (promptVersion) {
        extensionItems.push({
            url: "promptVersion",
            valueString: promptVersion
        });
    }
    return {
        url: PATIENT_SUMMARY_EXTENSION_URL,
        extension: extensionItems
    };
};
/**
 * Updates or adds patient summary extension; returns new extensions array (immutable).
 * @agentic @benchmark/core
 */ const updatePatientSummaryExtension = (existingExtensions, options)=>{
    const newSummaryExtension = buildPatientSummaryExtension(options);
    if (!existingExtensions) {
        return [
            newSummaryExtension
        ];
    }
    const otherExtensions = existingExtensions.filter((ext)=>ext.url !== PATIENT_SUMMARY_EXTENSION_URL);
    return [
        ...otherExtensions,
        newSummaryExtension
    ];
};
/**
 * Removes patient summary extension; returns new extensions array (immutable).
 * @agentic @benchmark/core
 */ const removePatientSummaryExtension = (existingExtensions)=>{
    if (!existingExtensions) {
        return undefined;
    }
    const filtered = existingExtensions.filter((ext)=>ext.url !== PATIENT_SUMMARY_EXTENSION_URL);
    return filtered.length > 0 ? filtered : undefined;
};

/**
 * Telecom Resolvers
 *
 * Pure functions that read existing Patient telecom data and return formatted
 * phone number information. These functions have no side effects and do not
 * interact with APIs.
 */ /**
 * Get formatted phone type label from FHIR use code
 */ const getPhoneTypeLabel = (use)=>{
    if (!use) return "Phone";
    // Map FHIR use codes to display labels
    const typeMap = {
        mobile: "Mobile",
        work: "Work",
        home: "Home",
        temp: "Temporary",
        old: "Old",
        fax: "Fax",
        pager: "Pager",
        other: "Other"
    };
    return typeMap[use.toLowerCase()] || use.charAt(0).toUpperCase() + use.slice(1);
};
/**
 * Determine priority for phone number sorting
 */ const determinePhonePriority = (telecom, isPrimary)=>{
    if (isPrimary) return 0; // Primary always first
    // Sort by use type priority
    const usePriority = {
        mobile: 1,
        work: 2,
        home: 3,
        temp: 4,
        other: 5,
        old: 6
    };
    const use = telecom.use?.toLowerCase() || "other";
    return usePriority[use] || 5;
};
/**
 * Get all active phone numbers from patient with metadata
 */ const getActivePhoneNumbers = (patient)=>{
    const phoneNumbers = [];
    // Get patient's direct phone numbers
    if (patient.telecom) {
        patient.telecom.filter((t)=>!t.period?.end && t.value && t.system === "phone").forEach((telecom, index)=>{
            // Only use index === 0 as fallback when no rank is specified
            const isPrimary = telecom.rank === 1 || index === 0 && !telecom.rank;
            phoneNumbers.push({
                value: telecom.value,
                use: telecom.use || "other",
                phoneType: getPhoneTypeLabel(telecom.use),
                isPrimary,
                priority: telecom.rank || determinePhonePriority(telecom, isPrimary)
            });
        });
    }
    // Get contact phone numbers
    if (patient.contact) {
        patient.contact.forEach((contact)=>{
            const contactName = contact.name ? `${contact.name.given?.[0] || ""} ${contact.name.family || ""}`.trim() : undefined;
            if (contact.telecom) {
                contact.telecom.filter((t)=>!t.period?.end && t.value && t.system === "phone").forEach((telecom)=>{
                    // Use FHIR-compliant relationship field only
                    // Fallback to relationship text if display not available
                    const relationship = contact.relationship?.[0]?.coding?.[0]?.display || contact.relationship?.[0]?.text;
                    phoneNumbers.push({
                        value: telecom.value,
                        use: telecom.use || "other",
                        phoneType: getPhoneTypeLabel(telecom.use),
                        roleLabel: relationship,
                        contactName,
                        isPrimary: false,
                        priority: telecom.rank || determinePhonePriority(telecom, false)
                    });
                });
            }
        });
    }
    // Sort by priority (primary first, then by use type)
    return phoneNumbers.sort((a, b)=>a.priority - b.priority);
};
/**
 * Get the patient's own primary phone number (first active `Patient.telecom`
 * phone by priority). Never returns a `Patient.contact` number: callers send
 * patient-addressed SMS, and a contact's phone belongs to someone else.
 */ const getPrimaryPhoneNumber = (patient)=>getActivePhoneNumbers({
        ...patient,
        contact: undefined
    })[0]?.value;

/**
 * CareTeam Participant Resolver
 *
 * Pure functions for extracting participant references from CareTeam resources
 * by SNOMED role code. Used by the assignment resolver and available for
 * general CareTeam lookups.
 * @agentic @benchmark/core
 */ /** SNOMED codes for CareTeam participant roles. @agentic @benchmark/core */ const CARE_TEAM_SNOMED_ROLES = {
    PATIENT: "116154003",
    LCSW: "224598009",
    ADVOCATE: "429577009"
};
/**
 * Resolve a CareTeam participant's member reference by SNOMED role code.
 * @agentic @benchmark/core
 */ const resolveCareTeamPractitionerRole = (careTeam, snomedRoleCode)=>{
    const participant = careTeam.participant?.find((p)=>p.role?.some((role)=>role.coding?.some((c)=>c.code === snomedRoleCode)));
    if (!participant?.member?.reference) return undefined;
    return {
        reference: participant.member.reference,
        display: participant.member.display ?? ""
    };
};
/**
 * Resolve the LCSW participant from a CareTeam.
 * @agentic @benchmark/core
 */ const resolveCareTeamLCSW = (careTeam)=>resolveCareTeamPractitionerRole(careTeam, CARE_TEAM_SNOMED_ROLES.LCSW);
/**
 * Resolve the Advocate participant from a CareTeam.
 * @agentic @benchmark/core
 */ const resolveCareTeamAdvocate = (careTeam)=>resolveCareTeamPractitionerRole(careTeam, CARE_TEAM_SNOMED_ROLES.ADVOCATE);

/**
 * Find a PractitionerRole in the registry that matches the given business role code.
 */ const findRoleInRegistry = (registry, roleCode)=>registry.find((pr)=>pr.code?.some((cc)=>cc.coding?.some((c)=>c.code === roleCode)));
/**
 * Extract PractitionerRole ID from resource. Returns the raw ID, not a full reference.
 */ const extractRoleId = (role)=>role.id;
/**
 * Build a display name for a PractitionerRole, falling back to the role code display.
 */ const extractRoleDisplay = (role)=>{
    const practitionerDisplay = role.practitioner?.display;
    if (practitionerDisplay) return practitionerDisplay;
    const roleCodeDisplay = role.code?.[0]?.coding?.[0]?.display;
    if (roleCodeDisplay) return roleCodeDisplay;
    return "";
};
/**
 * Resolve Task.owner for a given activity based on its assignment mode.
 *
 * Returns `ownerRef` as a bare PractitionerRole ID (not a full FHIR reference path).
 * Callers should construct the full reference as `PractitionerRole/${ownerRef}`.
 * When `ownerRef` is undefined, the task should be created without an owner (queue task).
 * @agentic @benchmark/core
 */ const resolveTaskAssignment = (context)=>{
    const mode = getActivityAssignmentMode(context.activityCode);
    switch(mode){
        case "unassigned-queue":
            return {
                mode
            };
        case "role-restricted":
            {
                const roleCode = getActivityAssignmentRoleCode(context.activityCode);
                if (!roleCode || !context.roleRegistry?.length) {
                    return {
                        mode
                    };
                }
                const matchedRole = findRoleInRegistry(context.roleRegistry, roleCode);
                if (!matchedRole) return {
                    mode
                };
                const id = extractRoleId(matchedRole);
                if (!id) return {
                    mode
                };
                return {
                    mode,
                    ownerRef: id,
                    ownerDisplay: extractRoleDisplay(matchedRole)
                };
            }
        case "care-team-assigned":
            {
                if (!context.patientCareTeam) return {
                    mode
                };
                const lcswRef = resolveCareTeamLCSW(context.patientCareTeam);
                if (!lcswRef) return {
                    mode
                };
                const practRoleId = lcswRef.reference.replace("PractitionerRole/", "");
                return {
                    mode,
                    ownerRef: practRoleId,
                    ownerDisplay: lcswRef.display
                };
            }
        case "primary-advocate":
            {
                const advocate = context.patientPrimaryAdvocate;
                if (!advocate?.ref) return {
                    mode
                };
                return {
                    mode,
                    ownerRef: advocate.ref,
                    ownerDisplay: advocate.display
                };
            }
        case "current-user":
            {
                if (!context.currentUserRole?.id) return {
                    mode
                };
                return {
                    mode,
                    ownerRef: context.currentUserRole.id,
                    ownerDisplay: extractRoleDisplay(context.currentUserRole)
                };
            }
        default:
            return {
                mode: "current-user"
            };
    }
};

/**
 * Task Owner Business Rules
 *
 * Pure functions that apply business-rule overrides to the assignment-mode
 * default. Rules are evaluated in order; first match wins.
 * @agentic @benchmark/core
 */ /**
 * Applies business-rule overrides to the resolved assignment result.
 *
 * Current rules:
 * - clinician-contact-only flag redirects unassigned-queue → primary-advocate
 *   (only when a primary advocate is available).
 * @agentic @benchmark/core
 */ const applyOwnerBusinessRules = (result, context)=>{
    if (result.mode === "unassigned-queue" && context.patientFlagCodes.includes("clinician-contact-only") && context.patientPrimaryAdvocate?.ref) {
        return {
            mode: "primary-advocate",
            ownerRef: context.patientPrimaryAdvocate.ref,
            ownerDisplay: context.patientPrimaryAdvocate.display,
            appliedRule: {
                rule: "clinician-contact-only",
                originalMode: result.mode
            }
        };
    }
    return result;
};

/**
 * Returns a meaningful display name for an appointment.
 * @agentic @benchmark/core
 */ const getAppointmentDisplayName = (appointment)=>`Appointment on ${formatDate(appointment.start)}`;
/**
 * Extracts referrer practitioner name from appointment participants.
 * @agentic @benchmark/core
 */ const getReferrerName = (appointment)=>{
    if (!appointment.participant) {
        return null;
    }
    // Look for referrer participant type
    const referrer = appointment.participant.find((participant)=>{
        // Check if this participant has referrer/referring provider type
        const isReferrer = participant.type?.some((typeCodeableConcept)=>typeCodeableConcept.coding?.some((coding)=>coding.code === "REF" || coding.display?.toLowerCase().includes("referrer") || coding.display?.toLowerCase().includes("referring")));
        return isReferrer && participant.actor?.reference && !participant.actor.reference.startsWith("Patient/");
    });
    if (referrer?.actor?.display) {
        return referrer.actor.display;
    }
    // Fallback: look for any participant with "requester" or "referring" in type or role
    const anyReferrer = appointment.participant.find((participant)=>{
        const hasReferrerRole = participant.type?.some((typeCodeableConcept)=>typeCodeableConcept.text?.toLowerCase().includes("referr") || typeCodeableConcept.coding?.some((coding)=>coding.display?.toLowerCase().includes("referr") || coding.display?.toLowerCase().includes("request")));
        return hasReferrerRole && participant.actor?.reference && !participant.actor.reference.startsWith("Patient/");
    });
    if (anyReferrer?.actor?.display) {
        return anyReferrer.actor.display;
    }
    return null;
};
/**
 * Extracts primary performer or service provider name from appointment participants.
 * @agentic @benchmark/core
 */ const getServiceProviderName = (appointment)=>{
    if (!appointment.participant || appointment.participant.length === 0) {
        return "No appointment participants found.";
    }
    // Look for Primary Performer participant type first
    const primaryPerformer = appointment.participant.find((participant)=>{
        // Check if this participant has Primary Performer type
        const isPrimaryPerformer = participant.type?.some((typeCodeableConcept)=>typeCodeableConcept.coding?.some((coding)=>coding.code === "PPRF" || coding.display?.toLowerCase().includes("primary performer")));
        return isPrimaryPerformer && participant.actor?.reference && !participant.actor.reference.startsWith("Patient/");
    });
    // If Primary Performer found, use it
    if (primaryPerformer?.actor?.display) {
        return primaryPerformer.actor.display;
    }
    // Fallback: Look for any provider that's not a patient
    const anyProvider = appointment.participant.find((participant)=>participant.actor?.reference && !participant.actor.reference.startsWith("Patient/") && (participant.actor.reference.startsWith("Practitioner/") || participant.actor.reference.startsWith("PractitionerRole/") || participant.actor.reference.startsWith("HealthcareService/") || participant.actor.reference.startsWith("Organization/") || participant.actor.reference.startsWith("Location/")));
    if (anyProvider?.actor?.display) {
        return anyProvider.actor.display;
    }
    // If no display name, try to extract meaningful info from reference
    if (anyProvider?.actor?.reference) {
        const reference = anyProvider.actor.reference;
        if (reference.startsWith("PractitionerRole/")) {
            return "Healthcare Provider"; // Could be enhanced to fetch the actual PractitionerRole resource
        }
        if (reference.startsWith("Practitioner/")) {
            return "Healthcare Provider"; // Could be enhanced to fetch the actual Practitioner resource
        }
        if (reference.startsWith("HealthcareService/")) {
            return "Healthcare Provider";
        }
        if (reference.startsWith("Organization/")) {
            return "Healthcare Provider";
        }
        if (reference.startsWith("Location/")) {
            return "Healthcare Provider";
        }
    }
    return "Unknown Provider";
};
/**
 * Extracts the primary performer participant from an appointment using FHIR participation type or fallback.
 * @agentic @benchmark/core
 */ function findPrimaryPerformerParticipant(appointment, currentUserRoleId) {
    // Strategy 1: Find by FHIR participation type code "PPRF" (primary performer)
    let performerParticipant = appointment.participant?.find((p)=>{
        const typeCode = p.type?.[0]?.coding?.[0]?.code;
        return typeCode === "PPRF";
    });
    // Strategy 2: If no PPRF found, find by reference type but exclude the current user's role
    if (!performerParticipant) {
        const practitionerRoleParticipants = appointment.participant?.filter((p)=>(p.actor?.reference?.startsWith("PractitionerRole/") || p.actor?.reference?.startsWith("Practitioner/")) && // Exclude the current user's referrer role if provided
            (!currentUserRoleId || !p.actor?.reference?.endsWith(currentUserRoleId)) && // Also exclude patient participants
            !p.actor?.reference?.startsWith("Patient/"));
        // Take the first non-referrer practitioner/role participant
        performerParticipant = practitionerRoleParticipants?.[0];
    }
    return performerParticipant;
}

/**
 * Returns shortened observation display name (e.g., "PHQ-4") from LOINC or legacy types.
 * @agentic @benchmark/core
 */ const getObservationDisplayName = (observation)=>{
    // First, try to find a matching observation type by code
    const observationCode = observation.code?.coding?.[0]?.code;
    if (observationCode) {
        // Check current observation types first
        const observationType = OBSERVATION_TYPES$1.find((type)=>type.code === observationCode);
        if (observationType) {
            return observationType.display;
        }
        // Check legacy observation types for backwards compatibility
        const legacyType = LEGACY_OBSERVATION_TYPES.find((type)=>type.code === observationCode);
        if (legacyType) {
            return legacyType.display;
        }
    }
    // Fallback to the original display, text, or default
    return observation.code?.coding?.[0]?.display || observation.code?.text || "Assessment";
};
/**
 * Groups observations by category for display.
 * @agentic @benchmark/core
 */ const groupObservationsByCategory = (observations)=>{
    const grouped = {};
    observations.forEach((obs)=>{
        const category = obs.category?.[0]?.coding?.[0]?.display || obs.category?.[0]?.coding?.[0]?.code || "Other";
        if (!grouped[category]) {
            grouped[category] = [];
        }
        grouped[category].push(obs);
    });
    return grouped;
};
/**
 * Extracts performer name from observation resource.
 * @agentic @benchmark/core
 */ const getObservationPerformerName = (observation)=>{
    // Observations use the performer field for the author/performer
    if (observation.performer?.[0]?.display) {
        return observation.performer[0].display;
    }
    return null;
};

const scoreValue = (obs)=>{
    if (typeof obs.valueInteger === "number") return String(obs.valueInteger);
    if (obs.valueQuantity?.value !== undefined) {
        return String(obs.valueQuantity.value);
    }
    return obs.valueString ?? undefined;
};
const effective = (obs)=>obs.effectiveDateTime ?? obs.effectivePeriod?.start;
const toNumber = (value)=>{
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
};
/**
 * Compute per-instrument assessment trends from a complete set of score
 * Observations. Groups by canonical `code` (falling back to display label),
 * takes the earliest score as baseline and the latest as current.
 * @agentic @benchmark/core
 */ const computeAssessmentTrends = (observations)=>{
    const groups = new Map();
    for (const obs of observations){
        // A score with no value or no effective date cannot be placed on a trend
        // timeline; an undated score must never sort as "latest" ahead of a dated
        // one, so it is excluded from the computation entirely.
        if (scoreValue(obs) === undefined) continue;
        if (!Number.isFinite(Date.parse(effective(obs) ?? ""))) continue;
        const key = obs.code?.coding?.[0]?.code ?? getObservationDisplayName(obs);
        const list = groups.get(key) ?? [];
        list.push(obs);
        groups.set(key, list);
    }
    const trends = [];
    for (const list of groups.values()){
        // Ascending by effective instant; undefined/invalid dates sort last so a
        // dated score is preferred as the extreme.
        const sorted = [
            ...list
        ].sort((a, b)=>{
            const ma = Date.parse(effective(a) ?? "");
            const mb = Date.parse(effective(b) ?? "");
            return (Number.isFinite(ma) ? ma : Number.POSITIVE_INFINITY) - (Number.isFinite(mb) ? mb : Number.POSITIVE_INFINITY);
        });
        const earliest = sorted[0];
        const latest = sorted[sorted.length - 1];
        const singleObservation = earliest === latest;
        const latestValue = scoreValue(latest);
        const baselineValue = scoreValue(earliest);
        const latestNum = toNumber(latestValue);
        const baselineNum = toNumber(baselineValue);
        let delta;
        if (latestNum !== undefined && baselineNum !== undefined) {
            const d = latestNum - baselineNum;
            delta = d > 0 ? `+${d}` : String(d);
        }
        trends.push({
            code: latest.code?.coding?.[0]?.code,
            label: getObservationDisplayName(latest),
            latestValue,
            latestDate: effective(latest),
            baselineValue,
            baselineDate: effective(earliest),
            delta,
            singleObservation
        });
    }
    return trends.sort((a, b)=>a.label.localeCompare(b.label));
};

/**
 * Statuses a paused care plan may legitimately be resumed into. A plan that was
 * completed, cancelled, revoked, or in an unknown state before the pause is not
 * restored by the resume path.
 * @agentic @benchmark/core
 */ const CARE_PLAN_RESUMABLE_STATUSES = [
    "draft",
    "active"
];
/**
 * Map Task.status to the unified activity display status.
 * @agentic @benchmark/core
 */ function mapTaskStatusToActivityStatus(taskStatus) {
    switch(taskStatus){
        case "completed":
            return "completed";
        case "in-progress":
        case "ready":
        case "requested":
        case "received":
        case "accepted":
            return "ready";
        case "cancelled":
        case "failed":
        case "entered-in-error":
            return "cancelled";
        case "on-hold":
            return "on-hold";
        case "draft":
        default:
            return "draft";
    }
}
/**
 * Returns human-readable label for an activity status.
 * @agentic @benchmark/core
 */ function getActivityStatusDisplay(status) {
    switch(status){
        case "completed":
            return "Completed";
        case "ready":
        case "requested":
        case "in-progress":
            return "Ready";
        case "draft":
        case "not-started":
        case "scheduled":
        case "unknown":
            return "Draft";
        case "cancelled":
        case "stopped":
            return "Cancelled";
        case "on-hold":
            return "On Hold";
        default:
            return "Draft";
    }
}
/**
 * Returns Mantine badge color for an activity status (purple palette).
 * @agentic @benchmark/core
 */ function getActivityStatusColor(status) {
    switch(status){
        case "completed":
            return "purple";
        case "ready":
        case "requested":
        case "in-progress":
            return "purple";
        case "on-hold":
            return "orange";
        case "cancelled":
        case "stopped":
        case "draft":
        case "not-started":
        case "scheduled":
        case "unknown":
        default:
            return "gray";
    }
}
/**
 * Resolve activity status from detail (legacy) or linked Task (Task-backed).
 * Task statuses map through the unified display model; legacy detail.status passes through.
 */ const resolveActivityStatus = (activity, tasksById)=>{
    if (activity.reference?.reference && tasksById) {
        const taskId = activity.reference.reference.startsWith("Task/") ? activity.reference.reference.slice(5) : activity.reference.reference;
        const task = tasksById.get(taskId);
        if (task) return mapTaskStatusToActivityStatus(task.status);
    }
    return activity.detail?.status;
};
/**
 * Resolve activity code from detail (legacy) or linked Task (Task-backed).
 */ const resolveActivityCode = (activity, tasksById)=>{
    const detailCode = activity.detail?.code?.coding?.[0]?.code;
    if (detailCode) return detailCode;
    if (activity.reference?.reference && tasksById) {
        const taskId = activity.reference.reference.startsWith("Task/") ? activity.reference.reference.slice(5) : activity.reference.reference;
        const task = tasksById.get(taskId);
        return task?.code?.coding?.[0]?.code;
    }
    return undefined;
};
/**
 * Extracts care plan type (intake, triage, cocm) from category coding.
 * @agentic @benchmark/core
 */ function getCarePlanType(carePlan) {
    const categoryCode = carePlan.category?.[0]?.coding?.[0]?.code;
    if (categoryCode === "intake" || categoryCode === "triage" || categoryCode === "cocm") {
        return categoryCode;
    }
    return undefined;
}
/**
 * Returns true if care plan status is "active".
 * @agentic @benchmark/core
 */ function isCarePlanActive(carePlan) {
    return carePlan.status === "active";
}
/**
 * Returns true if care plan status is "completed".
 * @agentic @benchmark/core
 */ function isCarePlanCompleted(carePlan) {
    return carePlan.status === "completed";
}
/**
 * Get care plan activity by code
 *
 * Legacy: finds by activity.detail.code.
 * Task-backed: pass tasksById to find activity whose linked Task has the code.
 */ function getCarePlanActivity(carePlan, activityCode, tasksById) {
    // Legacy: detail has code
    const byDetail = carePlan.activity?.find((activity)=>activity.detail?.code?.coding?.[0]?.code === activityCode);
    if (byDetail) return byDetail;
    // Task-backed: find activity whose linked Task has this code
    if (tasksById) {
        for (const activity of carePlan.activity ?? []){
            const ref = activity.reference?.reference;
            if (!ref) continue;
            const taskId = ref.startsWith("Task/") ? ref.slice(5) : ref;
            const task = tasksById.get(taskId);
            if (task?.code?.coding?.[0]?.code === activityCode) return activity;
        }
    }
    return undefined;
}
/**
 * Returns true if the specified care plan activity is completed.
 * @agentic @benchmark/core
 */ function isActivityCompleted(carePlan, activityCode, tasksById) {
    const activity = getCarePlanActivity(carePlan, activityCode, tasksById);
    if (!activity) return false;
    return resolveActivityStatus(activity, tasksById) === "completed";
}
/**
 * Returns count of completed activities in a care plan.
 * @agentic @benchmark/core
 */ function getCompletedActivitiesCount(carePlan, tasksById) {
    if (!carePlan.activity) return 0;
    return carePlan.activity.filter((activity)=>resolveActivityStatus(activity, tasksById) === "completed").length;
}
/**
 * Returns total number of activities in a care plan.
 * @agentic @benchmark/core
 */ function getTotalActivitiesCount(carePlan) {
    return carePlan.activity?.length || 0;
}
/**
 * Check if all activities in a CarePlan are completed
 *
 * For legacy (detail-based): checks activity.detail.status
 * For Task-backed: requires tasksById to check Task.status
 */ function areAllActivitiesCompleted(carePlan, tasksById) {
    if (!carePlan.activity || carePlan.activity.length === 0) return false;
    return carePlan.activity.every((activity)=>resolveActivityStatus(activity, tasksById) === "completed");
}
/**
 * Returns true if care plan can be marked completed (active with all activities done).
 * @agentic @benchmark/core
 */ function canCompleteCarePlan(carePlan, tasksById) {
    if (carePlan.status !== "active") {
        return false;
    }
    if (!carePlan.activity || carePlan.activity.length === 0) {
        return false;
    }
    return areAllActivitiesCompleted(carePlan, tasksById);
}
/**
 * Returns true if care plan can be activated (currently in draft status).
 * @agentic @benchmark/core
 */ function canActivateCarePlan(carePlan) {
    return carePlan.status === "draft";
}
/**
 * Returns display title from care plan category, or "Care Plan" as fallback.
 * @agentic @benchmark/core
 */ function getCarePlanDisplayTitle(carePlan) {
    const categoryCode = carePlan.category?.[0]?.coding?.[0]?.code || carePlan.category?.[0]?.text;
    if (categoryCode) {
        return getCarePlanCategoryDisplay(categoryCode);
    }
    return "Care Plan";
}
/**
 * Get the next incomplete activity in a CarePlan
 *
 * Returns the code of the first activity that is not completed/cancelled.
 * Supports both legacy (detail) and Task-backed (reference) activities.
 */ function getNextIncompleteActivity(carePlan, tasksById) {
    if (!carePlan.activity) return undefined;
    const incompleteActivity = carePlan.activity.find((activity)=>{
        const status = resolveActivityStatus(activity, tasksById);
        return status !== "completed" && status !== "cancelled" && status !== "stopped";
    });
    if (!incompleteActivity) return undefined;
    return resolveActivityCode(incompleteActivity, tasksById);
}
/**
 * Calculate CarePlan progress percentage
 *
 * Calculates the percentage of completed activities in a care plan.
 * Returns a value between 0 and 100, rounded to the nearest integer.
 *
 * OCPE Phase 5: Pass tasksById for Task-backed CarePlans to use Task.status.
 *
 * @param carePlan - Care plan to calculate progress for
 * @param tasksById - Optional map of Task id -> Task for Task-backed activities
 * @returns Progress percentage (0-100)
 */ function calculateCarePlanProgress(carePlan, tasksById) {
    if (!carePlan.activity || carePlan.activity.length === 0) return 0;
    const completedCount = carePlan.activity.filter((activity)=>resolveActivityStatus(activity, tasksById) === "completed").length;
    return Math.round(completedCount / carePlan.activity.length * 100);
}
/**
 * Get completed count for Task-backed or legacy CarePlans
 */ function getCompletedActivitiesCountWithTasks(carePlan, tasksById) {
    return getCompletedActivitiesCount(carePlan, tasksById);
}
/**
 * Get active care plans by type
 *
 * Filters care plans to return only active ones of the specified type.
 *
 * @param carePlans - Array of CarePlan resources
 * @param type - Care plan type to filter by
 * @returns Array of active care plans of the specified type
 */ function getActiveCarePlansByType(carePlans, type) {
    return carePlans.filter((plan)=>isCarePlanActive(plan) && getCarePlanType(plan) === type);
}
/**
 * Returns true if patient has an active care plan of the specified type.
 * @agentic @benchmark/core
 */ function hasActiveCarePlanOfType(carePlans, type) {
    return getActiveCarePlansByType(carePlans, type).length > 0;
}
/**
 * Filters care plans to active ones only.
 * @agentic @benchmark/core
 */ function getActiveCarePlans(carePlans) {
    return carePlans.filter(isCarePlanActive);
}
/**
 * Filters care plans to completed ones only.
 * @agentic @benchmark/core
 */ function getCompletedCarePlans(carePlans) {
    return carePlans.filter(isCarePlanCompleted);
}
/**
 * Analyzes care plans and returns patient care plan status summary.
 * @agentic @benchmark/core
 */ function resolvePatientCarePlanStatus(carePlans) {
    const activePlans = getActiveCarePlans(carePlans);
    const completedPlans = getCompletedCarePlans(carePlans);
    return {
        hasActiveIntake: hasActiveCarePlanOfType(carePlans, "intake"),
        hasActiveTriage: hasActiveCarePlanOfType(carePlans, "triage"),
        hasActiveCoCM: hasActiveCarePlanOfType(carePlans, "cocm"),
        hasAnyActivePlan: activePlans.length > 0,
        hasCompletedIntake: completedPlans.some((plan)=>getCarePlanType(plan) === "intake"),
        hasCompletedTriage: completedPlans.some((plan)=>getCarePlanType(plan) === "triage"),
        hasCompletedCoCM: completedPlans.some((plan)=>getCarePlanType(plan) === "cocm"),
        activePlanCount: activePlans.length,
        completedPlanCount: completedPlans.length
    };
}
/**
 * Derives engagement status (referred/engaged/discharged) from care plan state.
 * @agentic @benchmark/core
 */ function resolveEngagementStatusFromCarePlans(carePlans, currentEngagementStatus) {
    const status = resolvePatientCarePlanStatus(carePlans);
    if (status.hasActiveTriage || status.hasActiveCoCM) {
        return currentEngagementStatus === "engaged" ? null : "engaged";
    }
    // If patient has completed intake, they should be "engaged"
    if (status.hasCompletedIntake && !status.hasAnyActivePlan) {
        return currentEngagementStatus === "engaged" ? null : "engaged";
    }
    // If patient has completed CoCM and no active plans, they should be "discharged"
    if (status.hasCompletedCoCM && !status.hasAnyActivePlan) {
        return currentEngagementStatus === "discharged" ? null : "discharged";
    }
    // If patient has no active plans and no completed intake, they should be "referred"
    if (!status.hasAnyActivePlan && !status.hasCompletedIntake) {
        return currentEngagementStatus === "referred" ? null : "referred";
    }
    // Otherwise, maintain current status
    return null;
}
/**
 * Formats date as MM/DD/YY, avoiding timezone shift issues.
 * @agentic @benchmark/core
 */ function formatDateAsMMDDYY(dateInput) {
    if (!dateInput) {
        return "";
    }
    let year;
    let month;
    let day;
    if (typeof dateInput === "string") {
        // Extract date part from datetime strings (YYYY-MM-DDTHH:mm:ss...)
        // This avoids timezone conversion issues
        if (dateInput.includes("T")) {
            const datePart = dateInput.split("T")[0];
            const [y, m, d] = datePart.split("-").map(Number);
            if (!isNaN(y) && !isNaN(m) && !isNaN(d) && y > 0 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
                year = y;
                month = m;
                day = d;
            } else {
                return "";
            }
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
            // Date-only string (YYYY-MM-DD)
            const [y, m, d] = dateInput.split("-").map(Number);
            if (!isNaN(y) && !isNaN(m) && !isNaN(d) && y > 0 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
                year = y;
                month = m;
                day = d;
            } else {
                return "";
            }
        } else {
            // Fallback to Date parsing for other formats
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) {
                return "";
            }
            year = date.getFullYear();
            month = date.getMonth() + 1;
            day = date.getDate();
        }
    } else {
        // Date object - extract components directly
        year = dateInput.getFullYear();
        month = dateInput.getMonth() + 1;
        day = dateInput.getDate();
    }
    return `${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}/${String(year).slice(-2)}`;
}
/**
 * Extracts consent date from CoCM care plan extension.
 * @agentic @benchmark/core
 */ function getCoCMConsentDate(carePlan) {
    if (!carePlan.extension) {
        return undefined;
    }
    const consentExtension = carePlan.extension.find((ext)=>ext.url === COCM_CONSENT_DATE_EXTENSION_URL);
    return consentExtension?.valueDate;
}
/**
 * Returns the status a care plan held before it was paused, when the pause
 * bundle recorded one. Plans held before the marker existed return undefined,
 * and callers fall back to "active" (the historical resume behavior).
 * @agentic @benchmark/core
 */ function getPrePauseCarePlanStatus(carePlan) {
    const recorded = carePlan.extension?.find((ext)=>ext.url === CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL)?.valueCode;
    // "on-hold" would resume the plan into the state we are leaving; ignore it.
    if (!recorded || recorded === "on-hold") {
        return undefined;
    }
    return CARE_PLAN_RESUMABLE_STATUSES.includes(recorded) ? recorded : undefined;
}
/**
 * Builds care plan subtitle with start date and consent date (for CoCM).
 * @agentic @benchmark/core
 */ function getCarePlanSubtitle(carePlan) {
    const startDate = carePlan.period?.start;
    if (!startDate) {
        return undefined;
    }
    const formattedStartDate = formatDateAsMMDDYY(startDate);
    if (!formattedStartDate) {
        return undefined;
    }
    const planType = getCarePlanType(carePlan);
    if (planType === "cocm") {
        const consentDate = getCoCMConsentDate(carePlan);
        if (consentDate) {
            const formattedConsentDate = formatDateAsMMDDYY(consentDate);
            if (formattedConsentDate) {
                return `Started ${formattedStartDate} - Consented ${formattedConsentDate}`;
            }
        }
    }
    return `Started ${formattedStartDate}`;
}
/**
 * Returns true if care plan is CoCM type (by category code, system, or display).
 * @agentic @benchmark/core
 */ function isCoCMCarePlan(carePlan) {
    // Check if care plan category includes CoCM
    const cocmCategory = carePlan.category?.find((cat)=>cat.coding?.some((coding)=>coding.code === "cocm" || coding.system?.includes("cocm") || coding.display?.toLowerCase().includes("collaborative care")));
    return !!cocmCategory;
}

/**
 * Care-time Observation resolvers.
 *
 * Pure read helpers for classifying care-time `Observation`s. The key
 * predicate distinguishes entries auto-generated by the Dialpad webhook tee
 * from entries logged manually — used by the activity-log "AUTO" chip and by
 * the `connections-cli` backfill that reconciles historical duplicates.
 *
 * @agentic @benchmark/core
 */ /**
 * `Observation.method` codes the Dialpad webhook tee stamps on
 * auto-generated care-time entries (never produced by a manual surface).
 * @agentic @benchmark/core
 */ const AUTO_GENERATED_CARE_TIME_METHODS = new Set([
    "call",
    "sms"
]);
/**
 * Whether a care-time `Observation` was auto-generated by the Dialpad webhook
 * tee (as opposed to logged manually).
 *
 * An entry is auto-generated when BOTH hold:
 *  - its `method` is a tee-only code (`call` / `sms`), and
 *  - it is `derivedFrom` a `Communication` (the originating call/SMS).
 *
 * Requiring both avoids two false positives:
 *  - a manual progress-update entry that is `derivedFrom` its own progress
 *    `Communication` (method `progress-update`, not call/sms), and
 *  - a reconciled intake-call entry (method `intake-call`).
 *
 * Anything ambiguous is treated as manual so its minutes are never silently
 * dropped from billing.
 * @agentic @benchmark/core
 */ const isAutoGeneratedCareTime = (observation)=>{
    const method = observation.method?.coding?.find((coding)=>coding.code ? AUTO_GENERATED_CARE_TIME_METHODS.has(coding.code) : false);
    if (!method) return false;
    return (observation.derivedFrom ?? []).some((ref)=>ref.reference?.startsWith("Communication/"));
};

/**
 * FHIR-related URLs used throughout the application
 */ // HL7 FHIR Base URLs
// Organization Base URLs
const ORGANIZATION_BASE = "http://benchmarkhealth.com/fhir";
/**
 * Base URL for Benchmark Health custom FHIR extensions
 *
 * All custom StructureDefinition URLs should be derived from this base.
 * Use this constant to ensure consistency across the codebase.
 *
 * @example
 * ```ts
 * const myExtension = `${EXTENSION_BASE}/my-extension-name`;
 * // => "http://benchmarkhealth.com/fhir/StructureDefinition/my-extension-name"
 * ```
 */ const EXTENSION_BASE = `${ORGANIZATION_BASE}/StructureDefinition`;

/**
 * Current observation LOINC codes for assessment scores
 * Used for creating new Observation resources from questionnaire responses
 *
 * IMPORTANT: These codes should ONLY be used for Observation resources that contain
 * calculated total scores. For questionnaire definitions, use QUESTIONNAIRE_LOINC_CODES
 * from questionnaire.ts which contains the panel codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const OBSERVATION_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale Total Score - Organizational code (no official LOINC) */ ASRS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
    /** AUDIT-C Total Score - LOINC 75626-2 ✅ VALIDATED */ AUDIT_C_TOTAL_SCORE: "75626-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale Risk Level - Organizational code (no official LOINC) */ C_SSRS_SEVERITY: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
    /** GAD-7 Total Score - LOINC 70274-6 ✅ VALIDATED */ GAD7_TOTAL_SCORE: "70274-6",
    /** GAD-2 Total Score - Organizational code (the legacy LOINC 89204-7 is deprecated; the adaptive short check uses an org code) */ GAD2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
    /** UCLA-3 Three-Item Loneliness Scale Total Score - Organizational code (no official LOINC) */ UCLA3_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
    /** WSAS Work and Social Adjustment Scale Total Score - Organizational code (no official LOINC) */ WSAS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
    /** OCI-4 Obsessive-Compulsive Inventory Total Score - Organizational code (no official LOINC) */ OCI_4_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
    /** PC-PTSD-5 Total Score - LOINC 102017-1 ✅ VALIDATED */ PC_PTSD_5_TOTAL_SCORE: "102017-1",
    /** PSC-17 Pediatric Symptom Checklist Total Score - Organizational code (no official LOINC) */ PSC_17_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
    /** PHQ-9 Total Score - LOINC 44261-6 ✅ VALIDATED */ PHQ9_TOTAL_SCORE: "44261-6",
    /** PHQ-9A (Adolescent) Total Score - LOINC 89204-2 ✅ VALIDATED */ PHQ9A_TOTAL_SCORE: "89204-2",
    /** PHQ-4 Total Score - LOINC 70272-0 ✅ VALIDATED */ PHQ4_TOTAL_SCORE: "70272-0",
    /** SBIRT-ED Eating Disorder Screening Score - Organizational code (no official LOINC) */ SBIRT_ED_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
    /** TAPS-1 Substance Use Screening Score - Organizational code (no official LOINC) */ TAPS_1_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
    /** TAPS-2 Substance Use Assessment Score - Organizational code (no official LOINC) */ TAPS_2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
    /** WPAI Work Productivity and Activity Impairment Score - Organizational code (no official LOINC) */ WPAI_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-shp-score",
    /** WHO-5 Well-Being Index Total Score - Organizational code (no official LOINC) */ WHO5_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
    /** Health Leads Social Needs Screening Toolkit Score - Organizational code (no official LOINC) */ SDOH_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
    /** Mirah Complexity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_COMPLEXITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
    /** Mirah Severity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_SEVERITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
    /** NPS 0–10 likelihood score — organizational code (no official LOINC) */ NPS_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score"
};
/**
 * Current observation types for assessment scores
 * FHIR R4-compliant observation types from LOINC
 * http://hl7.org/fhir/ValueSet/observation-codes
 * These are TOTAL SCORE codes for questionnaire observations
 * This is the UNIFIED source that replaces the previous duplicated constants
 */ const OBSERVATION_TYPES = [
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
        display: "ASRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "asrs",
        description: "Adult ADHD Self-Report Scale",
        minValue: 0,
        maxValue: 24
    },
    {
        code: "75626-2",
        display: "AUDIT-C",
        system: "http://loinc.org",
        fieldKey: "audit-c",
        description: "Alcohol Use Disorders Identification Test - Consumption",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
        display: "GAD-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder 2-item short check",
        minValue: 0,
        maxValue: 6
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
        display: "UCLA-3",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "ucla-3",
        description: "Three-Item Loneliness Scale (UCLA)",
        minValue: 3,
        maxValue: 9
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
        display: "WSAS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wsas",
        description: "Work and Social Adjustment Scale",
        minValue: 0,
        maxValue: 40
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
        display: "C-SSRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "c-ssrs",
        description: "Columbia Suicide Severity Rating Scale",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "70274-6",
        display: "GAD-7",
        system: "http://loinc.org",
        fieldKey: "gad-7",
        description: "Generalized Anxiety Disorder-7",
        minValue: 0,
        maxValue: 21
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
        display: "OCI-4",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "oci-4",
        description: "Obsessive-Compulsive Inventory-4",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "102017-1",
        display: "PC-PTSD-5",
        system: "http://loinc.org",
        fieldKey: "pc-ptsd-5",
        description: "Primary Care PTSD Screen for DSM-5",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
        display: "PSC-17",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "psc-17",
        description: "Pediatric Symptom Checklist-17",
        minValue: 0,
        maxValue: 34
    },
    {
        code: "44261-6",
        display: "PHQ-9",
        system: "http://loinc.org",
        fieldKey: "phq-9",
        description: "Patient Health Questionnaire-9",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "89204-2",
        display: "PHQ-9A",
        system: "http://loinc.org",
        fieldKey: "phq-9a",
        description: "Patient Health Questionnaire-9 Modified for Teens",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "70272-0",
        display: "PHQ-4",
        system: "http://loinc.org",
        fieldKey: "phq-4",
        description: "Patient Health Questionnaire-4",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
        display: "PRAPARE",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "prapare",
        description: "Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
        display: "SBIRT-ED",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sbirt-ed",
        description: "SBIRT Eating Disorder Screener",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
        display: "TAPS-1",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-1",
        description: "TAPS Screener (Part 1)",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
        display: "TAPS-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-2",
        description: "TAPS Assessment (Part 2)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-impairment-score",
        display: "WPAI",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wpai",
        description: "Work Productivity and Activity Impairment",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
        display: "WHO-5",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "who-5",
        description: "World Health Organization Well-Being Index",
        minValue: 0,
        maxValue: 25
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
        display: "SDOH",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sdoh",
        description: "Social Needs Screening Toolkit (Health Leads)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
        display: "Mirah Complexity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-complexity",
        description: "Mirah Complexity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
        display: "Mirah Severity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-severity",
        description: "Mirah Severity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score",
        display: "NPS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "nps",
        description: "Net Promoter Score (0–10)",
        minValue: 0,
        maxValue: 10
    }
];
// Select options for forms
OBSERVATION_TYPES.map((type)=>({
        value: type.code,
        label: type.display
    }));
/**
 * Get the complete observation coding for a given LOINC code
 * Returns undefined if the code is not valid for observations
 */ function getObservationCoding(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}

/**
 * FHIR extension URL marking a care-time entry as excluded from CoCM billing.
 * Absent or `valueBoolean: false` means the entry is billable (backward
 * compatible — no historical data carries the extension). See the care-time
 * non-billable toggle plan (2026-07-23).
 * @agentic @benchmark/core
 */ const CARE_TIME_NON_BILLABLE_EXTENSION_URL = `${EXTENSION_BASE}/care-time-non-billable`;
/**
 * True only when the entry carries the non-billable extension set to `true`.
 * Any other shape (missing extension, `false`, absent `valueBoolean`) is
 * treated as billable.
 * @agentic @benchmark/core
 */ const isCareTimeNonBillable = (observation)=>(observation.extension ?? []).some((ext)=>ext.url === CARE_TIME_NON_BILLABLE_EXTENSION_URL && ext.valueBoolean === true);
/**
 * Source-surface method options carried on `Observation.method` (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_OPTIONS = [
    {
        code: "task-completion",
        display: "Task Completion"
    },
    {
        code: "progress-update",
        display: "Progress Update"
    },
    {
        code: "action-list-quick-add",
        display: "Action List Quick Add"
    },
    {
        code: "patient-profile-quick-add",
        display: "Patient Profile Quick Add"
    },
    {
        code: "intake-call",
        display: "Intake Call"
    },
    {
        code: "intake-documentation",
        display: "Intake Documentation"
    },
    {
        code: "cocm-followup",
        display: "CoCM Follow-Up"
    },
    {
        code: "general-interaction",
        display: "General Interaction"
    },
    {
        code: "call",
        display: "Call"
    },
    {
        code: "sms",
        display: "SMS"
    }
];
/**
 * Set of all valid care-time methods for fast membership checks.
 * @agentic @benchmark/core
 */ new Set(CARE_TIME_METHOD_OPTIONS.map((o)=>o.code));
/**
 * Complete roster of every assessment instrument that can enter a patient's
 * chart, mapped to the code its score Observation carries. This is the ONE
 * place that enumerates all instruments; billing eligibility is derived from it
 * as a denylist (see {@link BILLING_INELIGIBLE_ASSESSMENTS}) so a newly added
 * instrument is never silently billable — it must be classified here.
 *
 * Observations are clinical chart data first; billing eligibility is a separate
 * decision this module owns. A `code: ""` entry is a categorical instrument
 * (PGI-S, PGI-C, Safety Plan, Drug Use) that emits NO numeric score
 * Observation; every such instrument is billing-ineligible and must appear in
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}.
 *
 * This list is source-agnostic: eligibility reads Observations by code, so a
 * matching score unlocks billing whether it was written by the in-app
 * clinician encounter, a fixed remote survey link, or the adaptive screener.
 *
 * Two eligibility nuances the code-based list CANNOT enforce on its own:
 *   - AUDIT-C: only a FULLY administered AUDIT-C is billing-valid (2026-09-30).
 *     A gated "Never" first item is a genuine complete 0/12 and counts; an
 *     abandoned/partial administration must NOT emit a score Observation. Both
 *     share the one AUDIT-C score code, so completeness has to be enforced
 *     where the Observation is written, not here. Follow-up tracked on the
 *     billing-parity ticket.
 *   - Legacy PHQ-2 (deprecated LOINC 55758-7) is never billing-valid. It is not
 *     an administered instrument today, so it is intentionally absent from this
 *     roster; the lock test guards that its code never appears in the eligible
 *     list.
 * @agentic @benchmark/core
 */ const ALL_ASSESSMENTS = {
    PHQ4: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE,
    PHQ9: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE,
    PHQ9A: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE,
    GAD7: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE,
    GAD2: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE,
    PC_PTSD_5: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE,
    PSC_17: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE,
    C_SSRS: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY,
    ASRS: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE,
    OCI_4: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE,
    TAPS_1: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE,
    TAPS_2: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE,
    SBIRT_ED: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE,
    AUDIT_C: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE,
    UCLA3: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE,
    WSAS: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE,
    WPAI: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE,
    WHO5: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE,
    SDOH: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE,
    MIRAH_COMPLEXITY: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE,
    MIRAH_SEVERITY: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE,
    NPS: OBSERVATION_LOINC_CODES.NPS_SCORE,
    PGI_S: "",
    PGI_C: "",
    SAFETY_PLAN: "",
    DRUG_USE: ""
};
/**
 * Instruments that are administered but are NOT billing-valid for CoCM
 * eligibility (D18). This is the denylist over {@link ALL_ASSESSMENTS}: every
 * key here is subtracted to produce {@link BILLING_ELIGIBLE_ASSESSMENTS}. Each
 * entry names WHY it is excluded so the decision stays auditable. Confirmed
 * with clinical/compliance (2026-06-22, refined 2026-09-30).
 *
 * Note: an instrument absent from BOTH this denylist and the eligible set can
 * only happen if a new key is added to ALL_ASSESSMENTS without classifying it;
 * the exact-set lock test in `care-time.constants.test.ts` fails loudly in that
 * case rather than letting an unreviewed instrument become billable.
 * @agentic @benchmark/core
 */ const BILLING_INELIGIBLE_ASSESSMENTS = {
    PHQ4: "Ultra-brief combined screener; not a standalone severity measure.",
    GAD2: "Two-item anxiety short check; not billing-valid (opens GAD-7, which is).",
    UCLA3: "Loneliness scale; outside the billing-valid clinical set.",
    WSAS: "Everyday-functioning scale; not a clinical severity measure.",
    WPAI: "Work productivity/activity; functional, not clinical severity.",
    WHO5: "Well-being index; not a billing-valid severity measure.",
    SDOH: "Social-needs screen; not a clinical severity measure.",
    MIRAH_COMPLEXITY: "Vendor composite score; not a billing-valid instrument.",
    MIRAH_SEVERITY: "Vendor composite score; not a billing-valid instrument.",
    NPS: "Member-feedback likelihood score; not clinical.",
    PGI_S: "Categorical patient self-rating; no numeric score Observation.",
    PGI_C: "Categorical patient self-rating; no numeric score Observation.",
    SAFETY_PLAN: "Care-planning artifact; no score Observation.",
    DRUG_USE: "House-modified categorical drug screen; not billing-valid (2026-09-30) and no numeric score Observation."
};
// Eligible-by-policy = every instrument NOT on the denylist. Confirmed valid
// (2026-09-30): PHQ-9, PHQ-9A, GAD-7, PC-PTSD-5, PSC-17, C-SSRS, ASRS, OCI-4,
// TAPS-1, TAPS-2, SBIRT-ED, and AUDIT-C (full administration only). Every one
// carries a real score code; the `.filter` below is a guard, and the lock test
// asserts no eligible-by-policy instrument is categorical (`code: ""`) — a
// categorical instrument must be denied above, never silently dropped here.
const BILLING_ELIGIBLE_POLICY_KEYS = Object.keys(ALL_ASSESSMENTS).filter((key)=>!(key in BILLING_INELIGIBLE_ASSESSMENTS));
/**
 * The single billing-eligible assessment list, honored regardless of which
 * entry surface (in-app encounter, remote survey, adaptive screener) wrote the
 * score. Derived as {@link ALL_ASSESSMENTS} minus
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}, then narrowed to instruments that
 * actually emit a score Observation (non-empty code). Each entry's system is
 * resolved the same way `buildObservationFromScore` stamps the score
 * Observation (`getObservationCoding(code)?.system ?? "http://loinc.org"`), so
 * the eligibility search always matches what questionnaire completion writes.
 * Within the 6-week freshness window these unlock 99492 / 99493 / 99494.
 * @agentic @benchmark/core
 */ const BILLING_ELIGIBLE_ASSESSMENTS = BILLING_ELIGIBLE_POLICY_KEYS.map((key)=>ALL_ASSESSMENTS[key]).filter((code)=>code !== "").map((code)=>({
        system: getObservationCoding(code)?.system ?? "http://loinc.org",
        code
    }));
/**
 * Codes-only view of {@link BILLING_ELIGIBLE_ASSESSMENTS} for value comparisons
 * and tests.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>e.code);
/**
 * System-aware comma-delimited token search expression for the eligible
 * assessments (FHIR uses comma for OR within a single search parameter, and
 * `system|code` for the system-qualified token). Supports mixed LOINC and
 * organizational systems. Used by the eligibility service and the registry hook.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>`${e.system}|${e.code}`).join(",");

const methodLabel = (code)=>{
    if (!code) return "Care time";
    return CARE_TIME_METHOD_OPTIONS.find((o)=>o.code === code)?.display ?? code;
};
/**
 * Resolve the structured activity detail for a care-time Observation.
 * @agentic @benchmark/core
 */ const resolveCareTimeActivityDetail = (observation)=>({
        methodLabel: methodLabel(observation.method?.coding?.[0]?.code),
        contextLabels: [
            observation.focus?.[0]?.display,
            observation.basedOn?.[0]?.display
        ].filter((value)=>Boolean(value)),
        note: observation.note?.[0]?.text?.trim() || undefined
    });
/**
 * Compose a single-line activity label for the billing report time log. When a
 * task/care-plan context exists it is preferred (joined to the method) so a bare
 * "Task Completion" is never shown for an entry that has a described activity.
 * @agentic @benchmark/core
 */ const formatCareTimeActivityLabel = (detail)=>detail.contextLabels.length > 0 ? [
        detail.methodLabel,
        ...detail.contextLabels
    ].join(" · ") : detail.methodLabel;

/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-summary`;
/** Root complex-extension URL carrying submission + reopen audit. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-summary`;
/**
 * Relative sub-extension keys nested under
 * {@link COCM_BILLING_SUMMARY_EXTENSION_URL}.
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_EXT = {
    billingMonth: "billingMonth",
    submitted: "submitted",
    submittedAt: "submittedAt",
    submittedBy: "submittedBy",
    reopen: "reopen",
    reopenAt: "at",
    reopenReason: "reason",
    reopenBy: "by"
};
/**
 * `Observation.status` values that model the month lock:
 *  - `preliminary` = open (live, editable)
 *  - `final`       = closed / locked
 *  - `amended`     = reopened for correction
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_STATUS = {
    closed: "final",
    reopened: "amended"
};

const idFromRef = (ref)=>ref?.split("/").pop();
const rootExtensions = (obs)=>obs.extension?.find((e)=>e.url === COCM_BILLING_SUMMARY_EXTENSION_URL)?.extension ?? [];
const first = (exts, url)=>exts.find((e)=>e.url === url);
const lockStatusFromObservation = (obs)=>{
    if (obs.status === COCM_BILLING_SUMMARY_STATUS.closed) return "closed";
    if (obs.status === COCM_BILLING_SUMMARY_STATUS.reopened) return "reopened";
    return "open";
};
/**
 * Parse a persisted billing-summary Observation into a view model.
 * @agentic @benchmark/core
 */ const cocmBillingSummaryToView = (obs)=>{
    const exts = rootExtensions(obs);
    const identifier = obs.identifier?.find((i)=>i.system === COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM)?.value ?? "";
    const reopenHistory = exts.filter((e)=>e.url === COCM_BILLING_SUMMARY_EXT.reopen).map((entry)=>{
        const nested = entry.extension ?? [];
        const reopenByRef = first(nested, COCM_BILLING_SUMMARY_EXT.reopenBy)?.valueReference;
        return {
            at: first(nested, COCM_BILLING_SUMMARY_EXT.reopenAt)?.valueString ?? "",
            reason: first(nested, COCM_BILLING_SUMMARY_EXT.reopenReason)?.valueString,
            by: idFromRef(reopenByRef?.reference),
            byDisplay: reopenByRef?.display
        };
    });
    const lockStatus = lockStatusFromObservation(obs);
    const submittedByRef = first(exts, COCM_BILLING_SUMMARY_EXT.submittedBy)?.valueReference;
    const performerRef = obs.performer?.[0];
    return {
        id: obs.id,
        identifier,
        patientId: idFromRef(obs.subject?.reference) ?? "",
        patientDisplay: obs.subject?.display,
        performerRoleId: idFromRef(performerRef?.reference),
        performerDisplay: performerRef?.display,
        billingMonth: first(exts, COCM_BILLING_SUMMARY_EXT.billingMonth)?.valueString ?? "",
        attestedMinutes: obs.valueQuantity?.value ?? 0,
        clinicalSummary: obs.note?.[0]?.text,
        contributingObservationIds: (obs.derivedFrom ?? []).map((r)=>idFromRef(r.reference)).filter((id)=>Boolean(id)),
        submitted: first(exts, COCM_BILLING_SUMMARY_EXT.submitted)?.valueBoolean ?? false,
        submittedAt: first(exts, COCM_BILLING_SUMMARY_EXT.submittedAt)?.valueString,
        submittedBy: idFromRef(submittedByRef?.reference),
        submittedByDisplay: submittedByRef?.display,
        lockStatus,
        closed: lockStatus === "closed",
        reopenHistory
    };
};

/**
 * CoCM billing rules — versioned, frozen table.
 *
 * Implements the brief's "Billing Rules Table" verbatim:
 *  - Initial month, assessments present (D14 + D18)
 *  - Subsequent month, assessments present (D13 cascade applies)
 *  - Any month, no eligible assessment (G2214-only)
 *
 * Bounds use the brief's "16 lower bound uniformly" convention (see
 * Boundary precision flag). If the user later confirms 15 for
 * subsequent months, only the two cells in `SUBSEQUENT_WITH_ASSESSMENT`
 * and `NO_ASSESSMENT` change.
 *
 * @agentic @benchmark/core
 */ /** Version stamp included on any copied billing report (D11). @agentic @benchmark/core */ const COCM_BILLING_RULES_VERSION = "COCM_BILLING_RULES_V1";

const DIVIDER = "\u2500".repeat(32);
const LONG_DATE_FMT = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    timeZone: "UTC"
});
const GENERATED_FMT = new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
});
const DATE_TIME_FMT = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC"
});
// "06/24/26" — 2-digit slash date used in the billing-summary paragraph and
// assessment scores.
const SHORT_YY_FMT = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    timeZone: "UTC"
});
// "6/9" — time-log row date.
const MD_FMT = new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    timeZone: "UTC"
});
// "June" — month name only, for "accumulated in June".
const MONTH_LONG_FMT = new Intl.DateTimeFormat("en-US", {
    month: "long",
    timeZone: "UTC"
});
/** Format an ISO date with the given formatter; `undefined`/invalid → undefined. */ const fmt = (iso, formatter)=>{
    if (!iso) return undefined;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? undefined : formatter.format(new Date(ms));
};
const methodDisplay = (code)=>{
    if (!code) return "Care time";
    return CARE_TIME_METHOD_OPTIONS.find((o)=>o.code === code)?.display ?? code;
};
const observationEffective = (obs)=>obs.effectiveDateTime ?? obs.effectivePeriod?.start;
/**
 * Format a `YYYY-MM` billing month into a human label, e.g. "June 2026".
 * @agentic @benchmark/core
 */ const formatBillingMonthLabel = (billingMonth)=>{
    const [year, month] = billingMonth.split("-").map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return billingMonth;
    const label = new Intl.DateTimeFormat("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC"
    }).format(new Date(Date.UTC(year, month - 1, 1)));
    return label;
};
/** Trailing marker appended to non-billable entries in copied text. */ const NON_BILLABLE_MARKER = "(non-billable)";
/**
 * Resolve a care-time Observation to a structured activity-log row for the
 * billing tab's inline table. The single source of the date/method/minute
 * presentation shared with {@link formatCareTimeEntryLine}.
 * @agentic @benchmark/core
 */ const formatCareTimeEntryRow = (obs)=>{
    const effective = observationEffective(obs);
    return {
        when: effective ? DATE_TIME_FMT.format(new Date(effective)) : "—",
        activity: methodDisplay(obs.method?.coding?.[0]?.code),
        minutes: obs.valueQuantity?.value ?? 0,
        nonBillable: isCareTimeNonBillable(obs)
    };
};
/**
 * Format a single care-time entry as an activity-log line, e.g.
 * `Jun 5, 10:00 AM | Call | 23 min`. Delegates to {@link formatCareTimeEntryRow}
 * so the copied report and the billing-tab table read identically for the same
 * row (date + time, method, minutes). Non-billable entries carry a trailing
 * `(non-billable)` marker so the excluded minutes are visible in the copy.
 * @agentic @benchmark/core
 */ const formatCareTimeEntryLine = (obs)=>{
    const row = formatCareTimeEntryRow(obs);
    const base = `${row.when} | ${row.activity} | ${row.minutes} min`;
    return row.nonBillable ? `${base} ${NON_BILLABLE_MARKER}` : base;
};
/**
 * Serialize a CoCM billing report to plain text for clipboard copy.
 * @agentic @benchmark/core
 */ const formatBillingReport = (input)=>{
    const { billingMonth, billableMinutes, hasEligibleAssessment, latestAssessmentDate, packageDisplay, clinicalSummary, entries, generatedAt = new Date(), rulesVersion = COCM_BILLING_RULES_VERSION } = input;
    const assessmentStatus = hasEligibleAssessment && latestAssessmentDate ? `Last assessment: ${LONG_DATE_FMT.format(new Date(latestAssessmentDate))}` : "None in window";
    const billingCode = packageDisplay || "—";
    const entryLines = entries.map(formatCareTimeEntryLine);
    const lines = [
        `CoCM Billing Summary — ${formatBillingMonthLabel(billingMonth)}`,
        DIVIDER,
        // "Billable Time" is the windowed billable estimate (authoritative code +
        // amount are finalized in billing). It is intentionally not the sum of the
        // full activity log below (windowing excludes some logged minutes).
        `Billable Time (est.): ${billableMinutes} minutes`,
        `Assessment Status: ${assessmentStatus}`,
        `Billing Code: ${billingCode}`,
        "",
        "Clinical Summary",
        DIVIDER,
        "Assessment and Plan:",
        clinicalSummary?.trim() ? clinicalSummary.trim() : "[No summary recorded]",
        "",
        `Activity Log (${entries.length} ${entries.length === 1 ? "entry" : "entries"}):`,
        DIVIDER,
        ...entryLines,
        "",
        `Generated from Connections on ${GENERATED_FMT.format(generatedAt)}`,
        `Billing rules: ${rulesVersion}`
    ];
    return lines.join("\n");
};
const DASH = "—";
/**
 * 1-indexed calendar month of enrollment for a billing month.
 *
 * Enrolled and billed in the same calendar month → 1; enrolled the prior month
 * → 2; etc. Returns `undefined` when the enrollment date is missing/invalid or
 * falls after the billing month.
 * @agentic @benchmark/core
 */ const monthOfEnrollment = (enrollmentDate, billingMonth)=>{
    if (!enrollmentDate) return undefined;
    const enrolledMs = Date.parse(enrollmentDate);
    const [billYear, billMonth] = billingMonth.split("-").map(Number);
    if (Number.isNaN(enrolledMs) || !Number.isFinite(billYear) || !Number.isFinite(billMonth)) {
        return undefined;
    }
    const enrolled = new Date(enrolledMs);
    const delta = (billYear - enrolled.getUTCFullYear()) * 12 + (billMonth - 1 - enrolled.getUTCMonth()) + 1;
    return delta >= 1 ? delta : undefined;
};
/** Sanitize a note to a single clean cell (no pipes/newlines, collapsed spaces). */ const sanitizeNote = (note)=>note.replace(/[\r\n|]+/g, " ").replace(/\s+/g, " ").trim();
/**
 * Sum of the logged BILLABLE time-log minutes (the number the TOTAL line
 * reports). Non-billable entries are excluded so the copied report's total
 * matches the minutes that actually support the billing code.
 */ const loggedMinutes = (data)=>data.timeLog.reduce((sum, e)=>sum + (e.nonBillable ? 0 : e.minutes || 0), 0);
/** Sum of the logged time-log minutes flagged non-billable (the excluded split). */ const nonBillableLoggedMinutes = (data)=>data.timeLog.reduce((sum, e)=>sum + (e.nonBillable ? e.minutes || 0 : 0), 0);
const billingSummaryParagraph = (data)=>{
    const consent = fmt(data.consentDate, SHORT_YY_FMT);
    const enrolled = fmt(data.enrollmentDate, SHORT_YY_FMT);
    let sentence;
    if (consent && enrolled) {
        sentence = `${data.patientName} consented at ${consent} and was enrolled at ${enrolled}.`;
    } else if (enrolled) {
        sentence = `${data.patientName} was enrolled at ${enrolled}.`;
    } else if (consent) {
        sentence = `${data.patientName} consented at ${consent}.`;
    } else {
        sentence = `${data.patientName} — enrollment date not on record.`;
    }
    if (data.monthOfEnrollment !== undefined) {
        sentence += ` This is month ${data.monthOfEnrollment} of enrollment.`;
    }
    const monthName = fmt(`${data.billingMonth}-01`, MONTH_LONG_FMT) ?? data.billingMonth;
    const code = data.billingCode || DASH;
    // Use the logged total so the paragraph and the Time Log TOTAL agree (they
    // both describe the same minutes supporting the code); fall back to the
    // caller-supplied estimate only when there are no logged entries.
    const minutes = data.timeLog.length > 0 ? loggedMinutes(data) : data.billableMinutes;
    return [
        sentence,
        `A total of ${minutes} billable minutes were accumulated in ${monthName}, supporting billing code ${code}.`
    ];
};
const assessmentScoreLines = (data)=>{
    if (data.assessmentScores.length === 0) {
        return [
            "No clinical assessments on record."
        ];
    }
    return data.assessmentScores.map((score)=>{
        const latestDate = fmt(score.date, SHORT_YY_FMT);
        const head = latestDate ? `${score.label}: ${score.value} (on ${latestDate})` : `${score.label}: ${score.value}`;
        // Single observation → no follow-up to compare against.
        if (score.singleObservation || score.baselineValue === undefined) {
            return `${head} — baseline; no follow-up measurement this month`;
        }
        const baseDate = fmt(score.baselineDate, SHORT_YY_FMT);
        const deltaPart = score.delta ? ` (${score.delta})` : "";
        return `${head} — change from ${score.baselineValue} at baseline${baseDate ? ` (on ${baseDate})` : ""}${deltaPart}`;
    });
};
const timeLogLines = (data)=>{
    const rows = data.timeLog.map((entry)=>{
        const date = fmt(entry.date, MD_FMT) ?? DASH;
        const base = entry.note?.trim() ? `${date}  |  ${entry.activity}  |  "${sanitizeNote(entry.note)}"  |  ${entry.minutes} min` : `${date}  |  ${entry.activity}  |  ${entry.minutes} min`;
        return entry.nonBillable ? `${base}  |  ${NON_BILLABLE_MARKER}` : base;
    });
    const code = data.billingCode || DASH;
    rows.push(`TOTAL: ${loggedMinutes(data)} min — supports billing code ${code}`);
    const nonBillable = nonBillableLoggedMinutes(data);
    if (nonBillable > 0) {
        rows.push(`Non-billable (excluded): ${nonBillable} min`);
    }
    return rows;
};
/**
 * Serialize the clinician-facing CoCM billing report for clipboard copy. Pure
 * and deterministic (UTC/en-US). Structure: header (no Connections ID; MRN
 * retained) → Billing Summary → Summary → Clinical Assessments → Time Log. The
 * psych-consult statement lives inside the persisted Summary (produced by the
 * summarizer), not as a standalone section.
 * @agentic @benchmark/core
 */ const serializeBillingReport = (data)=>{
    const lines = [
        data.patientName,
        `MRN: ${data.mrn ?? DASH}`,
        `DOB: ${fmt(data.dob, GENERATED_FMT) ?? DASH}`,
        `Practice: ${data.practice ?? DASH}`,
        `Referring Provider: ${data.referringProvider ?? DASH}`,
        `Care Manager: ${data.careManager ?? DASH}`,
        `Date of Referral: ${fmt(data.referralDate, GENERATED_FMT) ?? DASH}`,
        `Date of Intake: ${fmt(data.intakeDate, GENERATED_FMT) ?? DASH}`,
        `CoCM Enrollment Date: ${fmt(data.enrollmentDate, GENERATED_FMT) ?? DASH}`,
        `Billing Period: ${formatBillingMonthLabel(data.billingMonth)}`,
        `Billing Code: ${data.billingCode || DASH}`,
        `Diagnosis Codes: ${data.diagnosisCodes.join("; ") || DASH}`
    ];
    const section = (title, body)=>{
        lines.push("", title, ...body);
    };
    section("Billing Summary", billingSummaryParagraph(data));
    section("Summary", [
        data.clinicalSummary?.trim() ? data.clinicalSummary.trim() : "[No clinical summary recorded]"
    ]);
    section("Clinical Assessments", assessmentScoreLines(data));
    section("Time Log (BHCM Clinical Time)", timeLogLines(data));
    return lines.join("\n");
};

/**
 * Returns display name for a clinical impression from coding or text.
 * @agentic @benchmark/core
 */ const getClinicalImpressionDisplayName = (clinicalImpression)=>{
    // Use the robust display resolution function that handles LOINC codes,
    // custom codes, and various fallback strategies
    if (clinicalImpression.code?.coding) {
        const displayName = getClinicalImpressionDisplayFromCoding(clinicalImpression.code.coding);
        if (displayName !== "Unknown Clinical Impression") {
            return displayName;
        }
    }
    // If no coding, try the text field
    const text = clinicalImpression.code?.text;
    if (text) {
        return text;
    }
    // Final fallback to generic format
    return "Clinical Note";
};
/**
 * Extracts author name from clinical impression assessor reference.
 * @agentic @benchmark/core
 */ const getClinicalImpressionAuthorName = (clinicalImpression)=>{
    // Clinical impressions use the assessor field for the author
    if (clinicalImpression.assessor?.display) {
        return clinicalImpression.assessor.display;
    }
    return null;
};

/**
 * Returns the call-state display ("Completed Call" | "Missed Call" |
 * "Voicemail") when the Communication carries a `call-state` category coding on
 * the canonical or legacy host, else `undefined`. Calls are the only archetype
 * that carries `call-state`, so this is what distinguishes a Dialpad call
 * outcome from a plain phone contact (progress-log / manual outreach by phone).
 * @agentic @benchmark/core
 */ const getCallStateDisplay = (communication)=>{
    for (const category of communication.category ?? []){
        for (const coding of category.coding ?? []){
            if (coding.system === CALL_STATE_SYSTEM || coding.system === LEGACY_CALL_STATE_SYSTEM) {
                const option = CALL_STATE_OPTIONS.find((o)=>o.code === coding.code);
                return option?.display ?? coding.display ?? "Call";
            }
        }
    }
    return undefined;
};
/**
 * Returns a display name for a Communication. Calls are labeled by their
 * `call-state` ("Completed Call" / "Missed Call" / "Voicemail"); a phone
 * `medium` without a call-state is a plain phone contact ("Phone Call") — it is
 * never labeled "Missed Call". Text/email/secure-message render as "Sent …".
 * @agentic @benchmark/core
 */ const getCommunicationDisplayName = (communication)=>{
    // Calls carry a call-state category coding; use it for the accurate label.
    const callStateDisplay = getCallStateDisplay(communication);
    if (callStateDisplay) return callStateDisplay;
    const mediumCode = communication.medium?.[0]?.coding?.[0]?.code;
    const mediumDisplay = communication.medium?.[0]?.coding?.[0]?.display;
    const mediumConfig = COMMUNICATION_MEDIUM_OPTIONS.find((option)=>option.code === mediumCode);
    // A phone medium WITHOUT a call-state is a plain phone contact (progress-log
    // or manual outreach), not a Dialpad call — must not read "Missed Call".
    if (mediumCode === "phone") {
        return "Phone Call";
    }
    if ([
        "text",
        "email",
        "secure-message"
    ].includes(mediumCode || "")) {
        const mediumName = mediumConfig?.display || mediumDisplay || "Communication";
        return `Sent ${mediumName}`;
    }
    return mediumConfig?.display || mediumDisplay || "Communication";
};
/**
 * Returns a short, UI-appropriate channel label for the timeline Channel column.
 * Distinct from getCommunicationDisplayName which is used in audit logs and copy text.
 * Phone contacts without a call-state show "Phone Call"; text/email/etc. show just
 * the channel name without the "Sent" prefix. Unknown medium falls back to "Outreach".
 * @agentic @benchmark/core
 */ const getCommunicationChannelLabel = (communication)=>{
    const mediumCode = communication.medium?.[0]?.coding?.[0]?.code;
    switch(mediumCode){
        case "phone":
            return "Phone Call";
        case "text":
            return "Text";
        case "email":
            return "Email";
        case "secure-message":
            return "Secure Message";
        case "fax":
            return "Fax";
        case "portal":
            return "Portal";
        default:
            return "Outreach";
    }
};
/**
 * Extracts sender name from communication resource.
 * @agentic @benchmark/core
 */ const getCommunicationSenderName = (communication)=>{
    // Communications use the sender field for the author/sender
    if (communication.sender?.display) {
        return communication.sender.display;
    }
    return null;
};
/**
 * Returns true if communication was created by Dialpad Import (meta.author.display).
 * @agentic @benchmark/core
 */ function isDialpadCommunication(communication) {
    const authorDisplay = communication.meta?.author?.display;
    return authorDisplay?.includes("Dialpad Import") ?? false;
}
/**
 * Returns true if a medium array carries the given v3-ParticipationMode channel code.
 * @agentic @benchmark/core
 */ const hasMediumChannel = (communication, channelCode)=>communication.medium?.some((m)=>m.coding?.some((coding)=>coding.system === COMMUNICATION_MEDIUM_SYSTEM && coding.code === channelCode)) ?? false;
/**
 * Returns true if the communication carries a given category code on a system.
 * @agentic @benchmark/core
 */ const hasCategory = (communication, system, code)=>communication.category?.some((cat)=>cat.coding?.some((coding)=>coding.system === system && coding.code === code)) ?? false;
/**
 * Returns true if communication is a phone call record. Recognizes the
 * canonical shape (a `call-state` category coding, or `patient-message`
 * category + phone `medium`) and the legacy `communication-channel|phone`
 * category, so it stays correct across the standardization backfill window.
 * @agentic @benchmark/core
 */ function isPhoneCallCommunication(communication) {
    const hasCallStateCategory = communication.category?.some((cat)=>cat.coding?.some((coding)=>coding.system === CALL_STATE_SYSTEM || coding.system === LEGACY_CALL_STATE_SYSTEM));
    if (hasCallStateCategory) return true;
    if (hasCategory(communication, COMMUNICATION_TYPE_SYSTEM, "patient-message") && hasMediumChannel(communication, "phone")) {
        return true;
    }
    return hasCategory(communication, LEGACY_COMMUNICATION_CHANNEL_SYSTEM, "phone");
}
/**
 * Returns true if communication is an SMS/text message. Recognizes the
 * canonical shape (`patient-message` category + text `medium`) and the legacy
 * `communication-channel|sms` category.
 * @agentic @benchmark/core
 */ function isSmsCommunication(communication) {
    if (hasCategory(communication, COMMUNICATION_TYPE_SYSTEM, "patient-message") && hasMediumChannel(communication, "text")) {
        return true;
    }
    return hasCategory(communication, LEGACY_COMMUNICATION_CHANNEL_SYSTEM, "sms");
}
/**
 * Returns true if communication should appear in patient timeline (excludes Dialpad SMS and phone records).
 * @agentic @benchmark/core
 */ function shouldShowCommunicationInTimeline(communication) {
    // Filter out Dialpad imported SMS
    if (isDialpadCommunication(communication)) {
        return false;
    }
    // Filter out phone call records (until UI is ready)
    if (isPhoneCallCommunication(communication)) {
        return false;
    }
    return true;
}

/**
 * Returns display name for an encounter (e.g., "Encounter on 1/15/25").
 * @agentic @benchmark/core
 */ const getEncounterDisplayName = (encounter)=>`Encounter on ${formatDate(encounter.period?.start)}`;
/**
 * Returns encounter description from type tags, with v1/v2 backwards compatibility.
 * @agentic @benchmark/core
 */ const getEncounterDescription = (encounterOrTags)=>{
    const encounterTags = Array.isArray(encounterOrTags) ? encounterOrTags : encounterOrTags.meta?.tag || [];
    // Use exact system matching instead of .includes()
    const typeTag = encounterTags.find((tag)=>tag.system === VALUE_SET.ENCOUNTER_TYPE);
    // Check for the new encounter types first (but ignore "unknown" types)
    if (typeTag?.code || typeTag?.display) {
        const code = typeTag.code?.toLowerCase();
        const display = typeTag.display?.toLowerCase();
        // Handle current encounter types (v2)
        if (code === "general-interaction" || display?.includes("general interaction")) {
            return "General Interaction";
        } else if (code === "clinical-intake-interaction" || display?.includes("clinical intake")) {
            return "Clinical Intake";
        } else if (code === "clinical-follow-up-interaction" || display?.includes("clinical follow-up")) {
            return "Followup";
        } else if (code === "cocm-follow-up-interaction" || display?.includes("cocm follow-up")) {
            // Must precede the legacy `follow-up` branch below: a CoCM follow-up's
            // stored display contains "follow-up", so without this it resolved to
            // the deprecated v1 label "Follow-Up Assessment".
            return "CoCM Followup";
        } else if (code === "unbilled-intake-interaction" || display?.includes("unbilled intake")) {
            return "Unbilled Intake";
        } else if (code === "unbilled-follow-up-interaction" || display?.includes("unbilled follow-up")) {
            return "Unbilled Followup";
        } else if (code === "case-management-interaction" || display?.includes("case management")) {
            return "Case Management";
        } else if (code === "initial" || display?.includes("initial")) {
            return "Initial Assessment";
        } else if (code === "check-in-evaluation" || display?.includes("check-in") || display?.includes("evaluation")) {
            return "Check-In/Evaluation";
        } else if (code === "follow-up" || display?.includes("follow-up") || display?.includes("f/u")) {
            return "Follow-Up Assessment";
        } else if (code === "outreach" || display?.includes("outreach")) {
            return "Outreach";
        }
        // Only return the display if it's not "unknown"
        if (typeTag.display && typeTag.display.toLowerCase() !== "unknown") {
            return typeTag.display;
        }
        // Only return formatted code if it's not "unknown"
        if (typeTag.code && typeTag.code.toLowerCase() !== "unknown") {
            return typeTag.code.replace(/-/g, " ").replace(/\b\w/g, (l)=>l.toUpperCase());
        }
    }
    const directionTag = encounterTags.find((tag)=>tag.system === VALUE_SET.ENCOUNTER_INOUT);
    const methodTag = encounterTags.find((tag)=>tag.system === VALUE_SET.ENCOUNTER_METHOD);
    if (directionTag?.display && methodTag?.display) {
        if (directionTag.display.toLowerCase().includes("outbound")) {
            switch(methodTag.display.toLowerCase()){
                case "text":
                    return "Sent a Text";
                case "phone":
                    return "Made a Phone Call";
                case "email":
                    return "Sent an Email";
                case "emr secure message":
                    return "Sent an EMR Secure Message";
                default:
                    return "Initiated Contact";
            }
        } else {
            // inbound
            switch(methodTag.display.toLowerCase()){
                case "text":
                    return "Received a Text";
                case "phone":
                    return "Received a Phone Call";
                case "email":
                    return "Received an Email";
                case "emr secure message":
                    return "Received an EMR Secure Message";
                default:
                    return "Received Contact";
            }
        }
    }
    // Fallback to just direction + method if both exist
    if (directionTag?.display && methodTag?.display) {
        return `${directionTag.display} ${methodTag.display}`;
    }
    // Fallback to method only
    if (methodTag?.display) {
        return methodTag.display;
    }
    // Fallback to direction only
    if (directionTag?.display) {
        return directionTag.display;
    }
    return "Encounter";
};
/**
 * Returns a human-friendly label describing a completed encounter, suitable for
 * embedding in task-completion context (`Task.statusReason.text`). Combines the
 * encounter's type description with its date when available — e.g.
 * "Clinical Intake on 1/15/25" or, absent a date, just "Clinical Intake".
 *
 * Prefer this over interpolating `encounter.id`: a raw UUID is meaningless to
 * users reading the task history, whereas the type + date is self-describing.
 * @agentic @benchmark/core
 */ const getEncounterCompletionLabel = (encounter)=>{
    const description = getEncounterDescription(encounter);
    const start = encounter.period?.start;
    return start ? `${description} on ${formatDate(start)}` : description;
};
/**
 * Extracts primary performer name from encounter participants.
 * @agentic @benchmark/core
 */ const getEncounterPrimaryPerformerName = (encounter)=>{
    if (!encounter.participant) {
        return null;
    }
    // Look for primary performer participant type first
    const primaryPerformer = encounter.participant.find((participant)=>{
        // Check if this participant has Primary Performer type
        const isPrimaryPerformer = participant.type?.some((typeCodeableConcept)=>typeCodeableConcept.coding?.some((coding)=>coding.code === "PPRF" || coding.display?.toLowerCase().includes("primary performer")));
        return isPrimaryPerformer && participant.individual?.reference && !participant.individual.reference.startsWith("Patient/");
    });
    // If Primary Performer found, use it
    if (primaryPerformer?.individual?.display) {
        return primaryPerformer.individual.display;
    }
    // Fallback: Look for any practitioner or practitioner role that's not a patient
    const anyProvider = encounter.participant.find((participant)=>participant.individual?.reference && !participant.individual.reference.startsWith("Patient/") && (participant.individual.reference.startsWith("Practitioner/") || participant.individual.reference.startsWith("PractitionerRole/")));
    if (anyProvider?.individual?.display) {
        return anyProvider.individual.display;
    }
    return null;
};
/**
 * Returns true if tag is an AI-generated tag (Aurora system).
 * @agentic @benchmark/core
 */ const isAiGeneratedTag = (tag)=>tag.system === AI_GENERATED_TAG_SYSTEM && !!tag.code && AI_GENERATED_TAG_CODES.has(tag.code);

/**
 * Returns display name for a flag from code or category.
 * @agentic @benchmark/core
 */ function getFlagDisplayName(flag) {
    const codeDisplay = flag.code?.coding?.[0]?.display || flag.code?.text;
    if (codeDisplay) return codeDisplay;
    const categoryDisplay = flag.category?.[0]?.coding?.[0]?.display;
    if (categoryDisplay) return `${categoryDisplay} Flag`;
    return `Flag ${flag.id ?? ""}`.trim();
}
/**
 * Returns true if flag represents patient pause (code "patient-paused").
 * @agentic @benchmark/core
 */ function isPauseFlag(flag) {
    const codings = flag.code?.coding || [];
    return codings.some((coding)=>coding.code === "patient-paused");
}
/**
 * Returns true when `now` falls inside `flag.period`. A missing period or
 * missing start/end bound is treated as open on that side (e.g. an absent
 * `period.end` means "indefinite"). Guards against pause/CoCM/minor flags
 * whose `status` was never flipped to `inactive` after the period elapsed
 * — we have no auto-deactivation bot or subscription yet, so consumers
 * must honor the period directly.
 * @agentic @benchmark/core
 */ function isFlagWithinActivePeriod(flag, now = new Date()) {
    const period = flag.period;
    if (!period) return true;
    const nowMs = now.getTime();
    if (period.start) {
        const startMs = new Date(period.start).getTime();
        if (Number.isFinite(startMs) && nowMs < startMs) return false;
    }
    if (period.end) {
        const endMs = new Date(period.end).getTime();
        if (Number.isFinite(endMs) && nowMs > endMs) return false;
    }
    return true;
}
/**
 * Returns true if flag represents CoCM enrollment.
 * @agentic @benchmark/core
 */ function isCoCMFlag(flag) {
    const code = flag.code?.coding?.[0];
    return code?.system === FLAG_LOCAL_CODE_SYSTEM && code?.code === "cocm-enrolled";
}
/**
 * Filters flags to CoCM enrollment flags only.
 * @agentic @benchmark/core
 */ function filterCoCMFlags(flags) {
    return flags.filter(isCoCMFlag);
}
/**
 * Returns true if flag represents minor (under 18) patient.
 * @agentic @benchmark/core
 */ function isMinorFlag(flag) {
    const code = flag.code?.coding?.[0];
    return code?.system === FLAG_LOCAL_CODE_SYSTEM && code?.code === "patient-under-18";
}
/**
 * Filters flags to minor (under 18) flags only.
 * @agentic @benchmark/core
 */ function filterMinorFlags(flags) {
    return flags.filter(isMinorFlag);
}

/**
 * Goal resolvers: pure functions that extract/transform Goal and progress Observation data.
 * @agentic @benchmark/core
 */ /**
 * Returns display text from goal category coding.
 * @agentic @benchmark/core
 */ const getGoalCategoryDisplay = (goal)=>{
    const category = goal.category?.[0];
    const coding = category?.coding?.[0];
    return coding?.display;
};
/**
 * Returns most recent GAS score from progress observations, excluding baseline (0).
 * @agentic @benchmark/core
 */ const getGoalCurrentScore = (goal, progressObservations)=>{
    if (!progressObservations || progressObservations.length === 0) {
        return null;
    }
    // Filter out baseline observations (valueInteger: 0) and invalid observations
    // Sort by effectiveDateTime descending (newest first)
    const sortedObservations = [
        ...progressObservations
    ].filter((obs)=>obs.effectiveDateTime && obs.valueInteger !== undefined && obs.valueInteger !== 0).sort((a, b)=>{
        const dateA = a.effectiveDateTime ? new Date(a.effectiveDateTime).getTime() : 0;
        const dateB = b.effectiveDateTime ? new Date(b.effectiveDateTime).getTime() : 0;
        return dateB - dateA; // Descending order
    });
    if (sortedObservations.length === 0) {
        return null;
    }
    const latestObservation = sortedObservations[0];
    return latestObservation.valueInteger ?? null;
};
/**
 * Returns effectiveDateTime of most recent progress observation.
 * @agentic @benchmark/core
 */ const getGoalLastUpdateDate = (goal, progressObservations)=>{
    if (!progressObservations || progressObservations.length === 0) {
        return null;
    }
    // Sort observations by effectiveDateTime descending (newest first)
    const sortedObservations = [
        ...progressObservations
    ].filter((obs)=>obs.effectiveDateTime).sort((a, b)=>{
        const dateA = a.effectiveDateTime ? new Date(a.effectiveDateTime).getTime() : 0;
        const dateB = b.effectiveDateTime ? new Date(b.effectiveDateTime).getTime() : 0;
        return dateB - dateA; // Descending order
    });
    if (sortedObservations.length === 0) {
        return null;
    }
    return sortedObservations[0].effectiveDateTime ?? null;
};
/**
 * Transforms progress observations into GoalProgressEntry array, sorted by date descending.
 * @agentic @benchmark/core
 */ const getGoalProgressHistory = (progressObservations)=>{
    if (!progressObservations || progressObservations.length === 0) {
        return [];
    }
    // Filter and map valid observations
    const entries = progressObservations.filter((obs)=>obs.effectiveDateTime && obs.valueInteger !== undefined && obs.id).map((obs)=>({
            date: obs.effectiveDateTime,
            score: obs.valueInteger,
            note: obs.note?.[0]?.text,
            observationId: obs.id,
            lastUpdated: obs.meta?.lastUpdated
        }));
    // Sort by date descending (newest first)
    // Secondary sort by meta.lastUpdated descending (most recently updated first)
    // This ensures stable ordering when multiple observations have the same effectiveDateTime
    entries.sort((a, b)=>{
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        // Primary sort by date
        if (dateB !== dateA) {
            return dateB - dateA; // Descending order
        }
        // Secondary sort by meta.lastUpdated when effectiveDateTime is equal
        const lastUpdatedA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
        const lastUpdatedB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
        if (lastUpdatedB !== lastUpdatedA) {
            return lastUpdatedB - lastUpdatedA; // Descending order (most recent first)
        }
        // Tertiary sort by observation ID (lexicographic) for stable ordering
        return (a.observationId || "").localeCompare(b.observationId || "");
    });
    return entries;
};
/**
 * Returns true if goal is overdue (>60 days since last update or start); false for completed.
 * @agentic @benchmark/core
 */ const isGoalOverdue = (goal, progressObservations)=>{
    // Completed goals are never overdue
    if (goal.lifecycleStatus === "completed") {
        return false;
    }
    const now = new Date();
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    // Check last update date from observations
    const lastUpdateDate = getGoalLastUpdateDate(goal, progressObservations);
    if (lastUpdateDate) {
        const lastUpdate = new Date(lastUpdateDate);
        return lastUpdate < sixtyDaysAgo;
    }
    // If no observations, check start date
    if (goal.startDate) {
        const startDate = new Date(goal.startDate);
        return startDate < sixtyDaysAgo;
    }
    // If no start date, consider it not overdue (can't determine)
    return false;
};
/**
 * Returns status badge color and label from goal lifecycle status.
 * @agentic @benchmark/core
 */ const getGoalStatusBadge = (goal)=>{
    const status = goal.lifecycleStatus;
    switch(status){
        case "active":
            return {
                color: "blue",
                label: "Active"
            };
        case "completed":
            return {
                color: "green",
                label: "Completed"
            };
        case "cancelled":
            return {
                color: "gray",
                label: "Cancelled"
            };
        case "on-hold":
            return {
                color: "yellow",
                label: "On Hold"
            };
        case "entered-in-error":
            return {
                color: "red",
                label: "Error"
            };
        case "proposed":
            return {
                color: "gray",
                label: "Proposed"
            };
        case "planned":
            return {
                color: "gray",
                label: "Planned"
            };
        case "accepted":
            return {
                color: "blue",
                label: "Accepted"
            };
        default:
            return {
                color: "gray",
                label: status || "Unknown"
            };
    }
};
/**
 * Returns average GAS score from progress observations.
 * @agentic @benchmark/core
 */ const getGoalAverageScore = (goal, progressObservations)=>{
    if (!progressObservations || progressObservations.length === 0) {
        return null;
    }
    // Filter to valid observations with valueInteger
    const validObservations = progressObservations.filter((obs)=>obs.valueInteger !== undefined);
    if (validObservations.length === 0) {
        return null;
    }
    // Calculate average
    const sum = validObservations.reduce((acc, obs)=>acc + (obs.valueInteger ?? 0), 0);
    const average = sum / validObservations.length;
    // Round to 1 decimal place
    return Math.round(average * 10) / 10;
};
/**
 * Returns note text from most recent progress observation.
 * @agentic @benchmark/core
 */ const getGoalLatestProgressNote = (goal, progressObservations)=>{
    if (!progressObservations || progressObservations.length === 0) {
        return null;
    }
    // Sort observations by effectiveDateTime descending (newest first)
    const sortedObservations = [
        ...progressObservations
    ].filter((obs)=>obs.effectiveDateTime).sort((a, b)=>{
        const dateA = a.effectiveDateTime ? new Date(a.effectiveDateTime).getTime() : 0;
        const dateB = b.effectiveDateTime ? new Date(b.effectiveDateTime).getTime() : 0;
        return dateB - dateA; // Descending order
    });
    if (sortedObservations.length === 0) {
        return null;
    }
    // Get note from most recent observation
    const latestObservation = sortedObservations[0];
    return latestObservation.note?.[0]?.text || null;
};

const MAX_TAG_LABELS = 8;
const getCharacteristicValue = (service, dimensionCode, valueSystem)=>{
    const characteristic = service.characteristic?.find((entry)=>entry.coding?.some((coding)=>coding.code === dimensionCode));
    const valueCoding = characteristic?.coding?.find((coding)=>coding.system === valueSystem);
    if (!valueCoding) return undefined;
    return {
        code: valueCoding.code,
        display: valueCoding.display
    };
};
const getMultiCodedValueDisplay = (concept)=>{
    if (concept.coding && concept.coding.length >= 2) {
        return concept.coding[1].display || concept.coding[1].code || concept.text || undefined;
    }
    return concept.coding?.[0]?.display || concept.coding?.[0]?.code || concept.text || undefined;
};
const collectTagLabels = (service)=>{
    const tags = [];
    const seen = new Set();
    const pushTag = (label)=>{
        const trimmed = label?.trim();
        if (!trimmed || seen.has(trimmed)) return;
        seen.add(trimmed);
        tags.push(trimmed);
    };
    service.type?.forEach((type)=>{
        pushTag(type.coding?.[0]?.display || type.coding?.[0]?.code || type.text);
    });
    const visitMode = getCharacteristicValue(service, "visitMode", HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE);
    pushTag(visitMode?.display || visitMode?.code);
    service.specialty?.forEach((specialty)=>{
        pushTag(getMultiCodedValueDisplay(specialty));
    });
    service.program?.forEach((program)=>{
        pushTag(getMultiCodedValueDisplay(program));
    });
    service.category?.forEach((category)=>{
        pushTag(category.coding?.[0]?.display || category.coding?.[0]?.code || category.text);
    });
    return tags.slice(0, MAX_TAG_LABELS);
};
/**
 * Builds a directory card view model from a HealthcareService resource.
 * @agentic @benchmark/core
 */ const getHealthcareServiceDirectoryCardModel = (service)=>{
    const availability = getCharacteristicValue(service, "availability", HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AVAILABILITY);
    const partnership = getCharacteristicValue(service, "partnership", HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_PARTNERSHIP);
    const visitMode = getCharacteristicValue(service, "visitMode", HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE);
    const verifiedExt = service.extension?.find((extension)=>extension.url === AVAILABILITY_LAST_VERIFIED_EXTENSION_URL);
    const lastVerifiedDate = verifiedExt?.extension?.find((extension)=>extension.url === "dateTime")?.valueDateTime;
    const lastVerifiedBy = verifiedExt?.extension?.find((extension)=>extension.url === "verifiedBy")?.valueString;
    const partnershipTier = partnership?.code === "primary" || partnership?.code === "secondary" ? partnership.code : null;
    const phone = service.telecom?.find((contact)=>contact.system === "phone" && contact.value);
    const website = service.telecom?.find((contact)=>contact.system === "url" && contact.value);
    const locationLabels = service.location?.map((location)=>location.display?.trim()).filter((display)=>Boolean(display)) ?? [];
    return {
        id: service.id ?? "",
        name: service.name?.trim() || "Unnamed Service",
        organizationDisplay: service.providedBy?.display?.trim() || undefined,
        serviceTypeDisplay: service.type?.[0]?.coding?.[0]?.display || service.type?.[0]?.coding?.[0]?.code || service.type?.[0]?.text || undefined,
        visitModeDisplay: visitMode?.display || visitMode?.code,
        availabilityCode: availability?.code,
        availabilityDisplay: availability?.display || availability?.code,
        lastVerifiedDate,
        lastVerifiedBy,
        partnershipTier,
        tagLabels: collectTagLabels(service),
        locationDisplay: locationLabels.length > 0 ? locationLabels.slice(0, 2).join(" · ") : undefined,
        referralMethodDisplay: service.referralMethod?.[0]?.coding?.[0]?.display || service.referralMethod?.[0]?.coding?.[0]?.code || service.referralMethod?.[0]?.text || undefined,
        phoneValue: phone?.value,
        websiteUrl: website?.value?.startsWith("http") ? website.value : website?.value ? `https://${website.value}` : undefined
    };
};
/**
 * Builds the contextual subtitle for Log Availability Update (org · type · mode).
 * @agentic @benchmark/core
 */ const getHealthcareServiceLogModalSubtitle = (service)=>{
    const model = getHealthcareServiceDirectoryCardModel(service);
    const parts = [
        model.organizationDisplay,
        model.serviceTypeDisplay,
        model.visitModeDisplay
    ].filter((part, index, array)=>Boolean(part) && array.indexOf(part) === index);
    return parts.length > 0 ? parts.join(" · ") : model.name;
};

/** Benchmark document-category code system (carried in `useContext.value`). @agentic @benchmark/core */ const LIBRARY_CATEGORY_SYSTEM = "https://benchmark.health/library-category";
/** HL7 R4 `definition-topic` CodeSystem URL (`Library.topic`). @agentic @benchmark/core */ const LIBRARY_TOPIC_SYSTEM = "http://terminology.hl7.org/CodeSystem/definition-topic";
/** `definition-topic` coding options selectable on the authoring form. @agentic @benchmark/core */ const LIBRARY_TOPIC_OPTIONS = [
    {
        code: "treatment",
        display: "Treatment"
    },
    {
        code: "education",
        display: "Education"
    },
    {
        code: "assessment",
        display: "Assessment"
    }
];
/**
 * Type guard narrowing an arbitrary string to a known document category.
 * @agentic @benchmark/core
 */ function isLibraryDocumentCategory(value) {
    return value === "clinical-sop" || value === "conversation-guide";
}
/** Returns the display label for a `definition-topic` code. @agentic @benchmark/core */ function getLibraryTopicDisplay(code) {
    return LIBRARY_TOPIC_OPTIONS.find((o)=>o.code === code)?.display ?? code;
}

/**
 * Returns the document category from a `Library`'s `useContext`, or undefined
 * for legacy/hand-authored resources without the category context.
 * @agentic @benchmark/core
 */ function getLibraryDocumentCategory(library) {
    for (const context of library.useContext ?? []){
        for (const coding of context.valueCodeableConcept?.coding ?? []){
            if (coding.system === LIBRARY_CATEGORY_SYSTEM && isLibraryDocumentCategory(coding.code)) {
                return coding.code;
            }
        }
    }
    return undefined;
}
/** Returns the `definition-topic` codes on a `Library`. @agentic @benchmark/core */ function getLibraryTopicCodes(library) {
    const codes = [];
    for (const topic of library.topic ?? []){
        for (const coding of topic.coding ?? []){
            if (coding.system === LIBRARY_TOPIC_SYSTEM && coding.code) {
                codes.push(coding.code);
            }
        }
    }
    return codes;
}
/** Returns human display labels for a `Library`'s topics. @agentic @benchmark/core */ function getLibraryTopicDisplays(library) {
    return getLibraryTopicCodes(library).map(getLibraryTopicDisplay);
}

/**
 * Returns display name for practitioner from formatted name, parts, or ID fallback.
 * @agentic @benchmark/core
 */ const getPractitionerDisplayName = (practitioner)=>{
    // Try formatted name first
    const formattedName = formatHumanName(practitioner.name?.[0]);
    if (formattedName) return formattedName;
    // Try individual name parts
    const name = practitioner.name?.[0];
    if (name) {
        const parts = [];
        if (name.prefix?.length) parts.push(...name.prefix);
        if (name.given?.length) parts.push(...name.given);
        if (name.family) parts.push(name.family);
        if (parts.length > 0) return parts.join(" ");
    }
    // Fallback to ID-based display
    return `Practitioner ${practitioner.id}`;
};

/** Returns the practitioner's active email, if any. @agentic @benchmark/core */ function getPractitionerEmail(practitioner) {
    return practitioner.telecom?.find((telecom)=>telecom.system === "email" && !!telecom.value)?.value;
}
/** Maps a Practitioner to a stakeholder (name + optional email). @agentic @benchmark/core */ function practitionerToStakeholder(practitioner) {
    return {
        name: getPractitionerDisplayName(practitioner),
        email: getPractitionerEmail(practitioner)
    };
}
/** Maps a stakeholder to a FHIR `ContactDetail`. @agentic @benchmark/core */ function stakeholderToContactDetail(stakeholder) {
    const contact = {
        name: stakeholder.name
    };
    if (stakeholder.email) {
        contact.telecom = [
            {
                system: "email",
                value: stakeholder.email
            }
        ];
    }
    return contact;
}
/** Maps a Practitioner directly to a `ContactDetail`. @agentic @benchmark/core */ function practitionerToContactDetail(practitioner) {
    return stakeholderToContactDetail(practitionerToStakeholder(practitioner));
}
/** Extracts the email from a `ContactDetail`'s telecom list. @agentic @benchmark/core */ function getContactDetailEmail(contact) {
    return contact.telecom?.find((telecom)=>telecom.system === "email" && !!telecom.value)?.value;
}

/**
 * Location Resolvers
 *
 * Pure functions that read existing Location data and return specific attributes.
 * These functions have no side effects and do not interact with APIs.
 */ /**
 * Returns location display name or fallback to "Location {id}".
 * @agentic @benchmark/core
 */ function getLocationDisplayName(location) {
    return location.name || `Location ${location.id}`;
}

/**
 * Get the patient's intake billing eligibility (set at schedule-intake), or
 * `undefined` when no eligibility tag is present ("no eligibility").
 * @agentic @benchmark/core
 */ function getPatientIntakeEligibility(patient) {
    const code = patient?.meta?.tag?.find((tag)=>tag.system === PATIENT_INTAKE_ELIGIBILITY_SYSTEM)?.code;
    return INTAKE_BILLING_FLAG_OPTIONS.some((o)=>o.code === code) ? code : undefined;
}

/**
 * Organization resolvers: pure functions that extract display data from Organization resources.
 * @agentic @benchmark/core
 */ /**
 * Returns organization display name or fallback to "Organization {id}".
 * @agentic @benchmark/core
 */ function getOrganizationDisplayName(organization) {
    return organization.name || `Organization ${organization.id}`;
}

/**
 * Engagement status codes that represent a patient who has been referred but
 * has not yet completed intake. Completing an intake should promote a patient
 * out of one of these states into "engaged".
 *
 * Includes the legacy V1 `initial-referral` code, which maps to the V2
 * `referred` code but may still be present on production patients.
 * @agentic @benchmark/core
 */ const PRE_INTAKE_ENGAGEMENT_STATUS_CODES = [
    "referred",
    "initial-referral"
];
/**
 * Get patient engagement status (V2 preferred, falls back to V1)
 */ function getEngagementStatus(patient) {
    if (!patient.meta?.tag) return null;
    // First check for V2 system tags (preferred)
    const v2Tags = patient.meta.tag.filter((tag)=>tag.system === PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v2Tags.length > 0) {
        const mostRecentV2Tag = v2Tags[v2Tags.length - 1];
        if (mostRecentV2Tag?.code) {
            // Try to find matching V2 option
            const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV2Tag.code);
            if (v2Option) {
                return {
                    code: v2Option.code,
                    display: v2Option.display,
                    system: PATIENT_ENGAGEMENT_STATUS_SYSTEM
                };
            }
            // Fallback to tag display if no matching option found
            return {
                code: mostRecentV2Tag.code,
                display: mostRecentV2Tag.display || mostRecentV2Tag.code,
                system: mostRecentV2Tag.system
            };
        }
    }
    // Fall back to V1 system tags (which may contain V2 codes in production)
    const v1Tags = patient.meta.tag.filter((tag)=>tag.system === LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v1Tags.length === 0) return null;
    const mostRecentV1Tag = v1Tags[v1Tags.length - 1];
    if (!mostRecentV1Tag?.code) return null;
    // Try to find matching option from V2 first (V2 codes stored under V1 system), then V1
    const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV1Tag.code);
    if (v2Option) {
        return {
            code: v2Option.code,
            display: v2Option.display,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM
        };
    }
    const v1Option = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV1Tag.code);
    if (v1Option) {
        return {
            code: v1Option.code,
            display: v1Option.display,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM
        };
    }
    // Fallback to tag display if no matching option found
    return {
        code: mostRecentV1Tag.code,
        display: mostRecentV1Tag.display || mostRecentV1Tag.code,
        system: mostRecentV1Tag.system
    };
}
/**
 * Returns engagement status with version (v1/v2) and migration suggestion.
 * @agentic @benchmark/core
 */ function getPatientEngagementStatus(patient) {
    if (!patient.meta?.tag) return null;
    // Prefer V2 system tags first (if they exist, they're the most current)
    const v2SystemTag = patient.meta.tag.find((tag)=>tag.system === PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v2SystemTag?.code) {
        const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v2SystemTag.code);
        if (statusOption) {
            return {
                code: statusOption.code,
                display: statusOption.display,
                system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v2"
            };
        }
        // Fallback: return V2 tag even if code doesn't match options (shouldn't happen, but prevents "Unknown")
        return {
            code: v2SystemTag.code,
            display: v2SystemTag.display || v2SystemTag.code,
            system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
            version: "v2"
        };
    }
    // Check for tags under V1 system URL (which may contain V2 codes)
    const v1SystemTag = patient.meta.tag.find((tag)=>tag.system === LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v1SystemTag?.code) {
        // First try to match as V2 code (current codes can be stored under V1 system)
        const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v1SystemTag.code);
        if (v2Option) {
            return {
                code: v2Option.code,
                display: v2Option.display,
                system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v2"
            };
        }
        // Fall back to V1 code matching
        const v1Option = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === v1SystemTag.code);
        if (v1Option) {
            return {
                code: v1Option.code,
                display: v1Option.display,
                system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                version: "v1",
                shouldMigrate: true
            };
        }
        // Fallback: return V1 tag even if code doesn't match options (prevents "Unknown")
        return {
            code: v1SystemTag.code,
            display: v1SystemTag.display || v1SystemTag.code,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
            version: "v1",
            shouldMigrate: true
        };
    }
    return null;
}
/**
 * Decide whether completing an intake should promote a patient to "engaged".
 *
 * "engaged" means the patient consented to services and completed intake, so a
 * finished intake must move a patient out of the pre-intake "referred" state.
 * Promotion is deliberately conservative:
 *  - Patients with no engagement status yet are promoted (a completed intake
 *    is proof of engagement).
 *  - Patients in the pre-intake `referred` / `initial-referral` state are
 *    promoted.
 *  - Every other status is left untouched. This protects already-progressed
 *    states (engaged / activated / graduated) and, critically, terminating
 *    states (declined / discharged / unreachable) from being resurrected by a
 *    stray intake completion.
 *
 * @agentic @benchmark/core
 */ function shouldEngagePatientOnIntakeCompletion(patient) {
    const status = getPatientEngagementStatus(patient);
    if (!status) return true;
    return PRE_INTAKE_ENGAGEMENT_STATUS_CODES.includes(status.code);
}

/**
 * Extracts pause reason from flag extension.
 * @agentic @benchmark/core
 */ function getPauseReason(flag) {
    const reasonExtension = flag.extension?.find((ext)=>ext.url === PAUSE_FLAG_REASON_EXTENSION_URL);
    return reasonExtension?.valueString;
}
/**
 * Returns remaining pause duration in days from flag period.end.
 * @agentic @benchmark/core
 */ function getRemainingPauseDays(flag) {
    if (!flag.period?.end) {
        return 0;
    }
    const endDate = new Date(flag.period.end);
    const now = new Date();
    const remainingMs = endDate.getTime() - now.getTime();
    const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
    return Math.max(0, remainingDays);
}
/**
 * Returns first pause flag whose code matches "patient-paused" AND whose
 * `period` (if set) covers the current time. Defends against flags whose
 * `status` is still active because no bot/subscription auto-deactivates
 * them when the period elapses.
 * @agentic @benchmark/core
 */ function getPatientPauseStatus(activeFlags) {
    const pauseFlags = activeFlags.filter((flag)=>isPauseFlag(flag) && isFlagWithinActivePeriod(flag));
    return pauseFlags.length > 0 ? pauseFlags[0] : null;
}
/**
 * Returns true when a pause Flag's `period.end` is strictly in the past.
 * A flag without a `period.end` is treated as indefinite (never expired).
 * @agentic @benchmark/core
 */ function isPauseFlagExpired(flag, now = new Date()) {
    const end = flag.period?.end;
    if (!end) {
        return false;
    }
    const endMs = new Date(end).getTime();
    return Number.isFinite(endMs) && endMs < now.getTime();
}
/**
 * Resolves a period bound to epoch ms, using `fallback` when absent/invalid.
 */ const periodBound = (value, fallback)=>{
    if (!value) {
        return fallback;
    }
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : fallback;
};
/**
 * Returns true when two pause Flags' periods intersect. A missing start is
 * treated as open-started and a missing end as open-ended.
 * @agentic @benchmark/core
 */ function pauseFlagsOverlap(a, b) {
    const aStart = periodBound(a.period?.start, -Infinity);
    const aEnd = periodBound(a.period?.end, Infinity);
    const bStart = periodBound(b.period?.start, -Infinity);
    const bEnd = periodBound(b.period?.end, Infinity);
    return aStart <= bEnd && bStart <= aEnd;
}
/**
 * Merges the periods of overlapping pause Flags into the span they cover
 * together, so a chain of overlapping pauses reads as one continuous episode.
 * @agentic @benchmark/core
 */ function mergePauseFlagPeriods(flags) {
    const starts = flags.map((flag)=>flag.period?.start).filter((value)=>Boolean(value)).map((value)=>new Date(value).getTime()).filter((ms)=>Number.isFinite(ms));
    const ends = flags.map((flag)=>flag.period?.end).filter((value)=>Boolean(value)).map((value)=>new Date(value).getTime()).filter((ms)=>Number.isFinite(ms));
    return {
        ...starts.length > 0 ? {
            start: new Date(Math.min(...starts)).toISOString()
        } : {},
        ...ends.length > 0 ? {
            end: new Date(Math.max(...ends)).toISOString()
        } : {}
    };
}

/**
 * Returns PlanDefinition template by type.
 * @agentic @benchmark/core
 */ const getPlanDefinitionByType = (type)=>{
    switch(type){
        case "intake":
            return INTAKE_PLAN_DEFINITION;
        case "triage":
            return TRIAGE_PLAN_DEFINITION;
        case "cocm":
            return COCM_PLAN_DEFINITION;
        default:
            return undefined;
    }
};
/**
 * Extracts all action codes from a PlanDefinition.
 * @agentic @benchmark/core
 */ const getPlanDefinitionActionCodes = (planDefinition)=>{
    if (!planDefinition.action) {
        return [];
    }
    return planDefinition.action.map((action)=>action.code?.[0]?.coding?.[0]?.code).filter((code)=>code !== undefined);
};
/**
 * Finds PlanDefinition action by code.
 * @agentic @benchmark/core
 */ const getPlanDefinitionAction = (planDefinition, actionCode)=>planDefinition.action?.find((action)=>action.code?.[0]?.coding?.[0]?.code === actionCode);
/**
 * Returns true if action has a predecessor that must be completed first.
 * @agentic @benchmark/core
 */ const actionHasPredecessor = (planDefinition, actionCode)=>{
    const action = getPlanDefinitionAction(planDefinition, actionCode);
    if (!action || !action.relatedAction) {
        return false;
    }
    return action.relatedAction.some((related)=>related.relationship === "after");
};
/**
 * Returns predecessor action code for an action with "after" relationship.
 * @agentic @benchmark/core
 */ const getPredecessorActionCode = (planDefinition, actionCode)=>{
    const action = getPlanDefinitionAction(planDefinition, actionCode);
    if (!action || !action.relatedAction) {
        return undefined;
    }
    const predecessorRelation = action.relatedAction.find((related)=>related.relationship === "after");
    return predecessorRelation?.actionId;
};

/** Gets questionnaire configuration by URL. @agentic @benchmark/core */ const getQuestionnaireConfig = (url)=>Object.values(STANDARD_QUESTIONNAIRES).find((config)=>config.url === url);
/** Gets enhanced questionnaire configuration with questions included. @agentic @benchmark/core */ const getEnhancedQuestionnaireConfig = (url)=>{
    const baseConfig = getQuestionnaireConfig(url);
    if (!baseConfig) return null;
    // Items come from the single canonical registry source (no per-call map).
    return {
        ...baseConfig,
        questions: getQuestionnaireItems(url)
    };
};

/**
 * Returns display name for questionnaire from title, name, config, or ID fallback.
 * @agentic @benchmark/core
 */ const getQuestionnaireDisplayName = (questionnaire)=>{
    if (questionnaire.title) return questionnaire.title;
    if (questionnaire.name) return questionnaire.name;
    if (questionnaire.url) {
        const config = getQuestionnaireConfig(questionnaire.url);
        if (config?.title) return config.title;
        if (config?.name) return config.name;
    }
    return `Questionnaire ${questionnaire.id ?? ""}`.trim();
};

/**
 * Returns display name for questionnaire response (e.g., "PHQ-4 - 1/15/25").
 * @agentic @benchmark/core
 */ const getQuestionnaireResponseDisplayName = (questionnaireResponse, questionnaire)=>{
    // Try to get questionnaire name first
    let questionnaireName = "Assessment";
    if (questionnaire) {
        questionnaireName = getQuestionnaireDisplayName(questionnaire);
    } else if (questionnaireResponse.questionnaire) {
        // Check if it's a standard assessment
        const config = getQuestionnaireConfig(questionnaireResponse.questionnaire);
        if (config) {
            questionnaireName = config.name;
        }
    }
    // Format with date if available
    if (questionnaireResponse.authored) {
        const date = new Date(questionnaireResponse.authored);
        // Check if the date is valid
        if (!isNaN(date.getTime())) {
            return `${questionnaireName} - ${date.toLocaleDateString()}`;
        }
    }
    return questionnaireName;
};

const flattenItems = (items)=>items.flatMap((item)=>item.type === "group" && item.item ? [
            item,
            ...flattenItems(item.item)
        ] : [
            item
        ]);
const answered = (answer)=>answer.value !== undefined || answer.valueString !== undefined && answer.valueString.length > 0;
/**
 * Evaluate completion against the current canonical questionnaire definition.
 * Gated-out required items are excluded when their gate parent is answered 0.
 *
 * @agentic @benchmark/core
 */ const evaluateQuestionnaireCompletion = (questionnaireUrl, answers, options = {})=>{
    const items = getQuestionnaireItems(questionnaireUrl);
    if (!items) {
        return {
            supported: false,
            reason: "unknown-questionnaire"
        };
    }
    const flattened = flattenItems(items);
    const itemByLinkId = new Map(flattened.map((item)=>[
            item.linkId,
            item
        ]));
    for (const answer of answers){
        if (!itemByLinkId.has(answer.linkId)) {
            return {
                supported: false,
                reason: "unknown-link-id"
            };
        }
        if (!answered(answer) && answer.coding !== undefined) {
            return {
                supported: false,
                reason: "unsupported-answer"
            };
        }
    }
    const valueByLinkId = new Map(answers.map((answer)=>[
            answer.linkId,
            answer.value
        ]));
    const gateForByLinkId = new Map();
    for (const item of flattened){
        if (item.gateFor) {
            gateForByLinkId.set(item.linkId, item.gateFor);
        }
    }
    const hidden = new Set();
    for (const item of flattened){
        if (!item.gateFor || valueByLinkId.get(item.linkId) !== 0) {
            continue;
        }
        const queue = [
            ...item.gateFor
        ];
        while(queue.length > 0){
            const linkId = queue.shift();
            if (hidden.has(linkId)) {
                continue;
            }
            hidden.add(linkId);
            queue.push(...gateForByLinkId.get(linkId) ?? []);
        }
    }
    const answerByLinkId = new Map(answers.filter(answered).map((answer)=>[
            answer.linkId,
            answer
        ]));
    const missingRequired = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId) && !answerByLinkId.has(item.linkId)).map((item)=>item.linkId);
    const activeRequiredCount = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId)).length;
    return {
        supported: true,
        complete: missingRequired.length === 0 && (activeRequiredCount > 0 || options.declaredComplete === true),
        missingRequired
    };
};

/** `meta.tag` system that marks a QR as part of an adaptive-screener sitting. */ const ADAPTIVE_SCREENER_SESSION_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/adaptive-screener-session";
/** Extension url carrying why the section was asked (F7). */ const ADAPTIVE_SCREENER_REASON_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-reason";
/** Extension url carrying whether the sitting was the patient's baseline. */ const ADAPTIVE_SCREENER_BASELINE_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-baseline";
/** Extension url carrying how the section settled — answered vs a decline kind (D8). */ const ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-settlement";
/**
 * Extension url carrying the link ids the patient explicitly skipped (asked and
 * not answered — PRD §8.4). Skips are deliberately persisted as an extension,
 * never as answerless `item` entries: item presence would read as "content" to
 * cross-visit memory (flipping a later administration to follow-up against
 * D12) and would complicate every raw-item consumer, while an extension is
 * invisible to memory, completion, and scoring.
 */ const ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-skipped-items";
/**
 * Extension url freezing the administration's composition inputs at the first
 * persisted write: which short-check sections were opened-in-full by memory
 * and whether the rating anchor was established. Without this, every request
 * re-derives composition from the patient's *current* prior responses, so a
 * prior sitting completing mid-administration would flip the composed section
 * list (and kind) between two saves of the same sitting. The marker makes the
 * first write's derivation authoritative for the administration's lifetime.
 */ const ADAPTIVE_SCREENER_COMPOSITION_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-composition";
/** Extension urls capturing the suicide-risk acknowledgement author + time (D13). */ const ADAPTIVE_SCREENER_ACK_AT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-at";
const ADAPTIVE_SCREENER_ACK_BY_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-by";
/**
 * Read the adaptive-screener marker off a QR, or null when the QR was not part
 * of an adaptive-screener sitting.
 *
 * @agentic @benchmark/core
 */ /**
 * Parse the frozen-composition extension fail-soft: a corrupt or unexpected
 * payload yields `undefined` (the caller re-derives from prior responses)
 * rather than throwing on a persisted-value read.
 */ const parseComposition = (raw)=>{
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) return undefined;
        const candidate = parsed;
        if (!Array.isArray(candidate.openedInFullBefore) || !candidate.openedInFullBefore.every((key)=>typeof key === "string") || typeof candidate.ratingAnchorEstablished !== "boolean") {
            return undefined;
        }
        return {
            openedInFullBefore: candidate.openedInFullBefore,
            ratingAnchorEstablished: candidate.ratingAnchorEstablished
        };
    } catch  {
        return undefined;
    }
};
const readAdaptiveScreenerMarker = (qr)=>{
    const sessionTag = (qr.meta?.tag ?? []).find((tag)=>tag.system === ADAPTIVE_SCREENER_SESSION_SYSTEM);
    if (!sessionTag?.code) {
        return null;
    }
    const findExt = (url)=>(qr.extension ?? []).find((ext)=>ext.url === url);
    const skippedRaw = findExt(ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION)?.valueString;
    const skippedLinkIds = skippedRaw ? skippedRaw.split(/\s+/).filter((id)=>id.length > 0) : undefined;
    const composition = parseComposition(findExt(ADAPTIVE_SCREENER_COMPOSITION_EXTENSION)?.valueString);
    return {
        sessionId: sessionTag.code,
        baseline: findExt(ADAPTIVE_SCREENER_BASELINE_EXTENSION)?.valueBoolean === true,
        reasonCode: findExt(ADAPTIVE_SCREENER_REASON_EXTENSION)?.valueCode,
        settlement: findExt(ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION)?.valueCode,
        ...skippedLinkIds && skippedLinkIds.length > 0 ? {
            skippedLinkIds
        } : {},
        ...composition ? {
            composition
        } : {},
        acknowledgedAt: findExt(ADAPTIVE_SCREENER_ACK_AT_EXTENSION)?.valueDateTime,
        acknowledgedBy: findExt(ADAPTIVE_SCREENER_ACK_BY_EXTENSION)?.valueString
    };
};
new Map(LIKERT_FREQUENCY_OPTIONS.map((option)=>[
        option.coding.code,
        option.value
    ]));

/**
 * LOINC codes for questionnaire panels/forms (used for search/discovery and canonical URL construction)
 * These identify the questionnaire definition itself, NOT the scores/observations
 *
 * NOTE: These are used for:
 * 1. Searching/discovering questionnaires in FHIR servers
 * 2. Constructing canonical URLs for Questionnaire.url
 * 3. Initial questionnaire creation
 *
 * IMPORTANT: Once a questionnaire is fetched, prefer extracting LOINC codes
 * dynamically from the actual FHIR resource rather than using these static values.
 *
 * Only includes assessments with official LOINC questionnaire panel codes.
 * Questionnaires without official LOINC panel codes use organizational URLs.
 *
 * IMPORTANT: These codes should ONLY be used for Questionnaire and QuestionnaireResponse
 * resources. For total score observations, use OBSERVATION_LOINC_CODES from
 * observation.ts which contains the score codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const QUESTIONNAIRE_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale - LOINC 62750-5 ✅ VALIDATED */ ASRS: "62750-5",
    /** AUDIT-C Alcohol Screening Panel - LOINC 72109-2 ✅ VALIDATED */ AUDIT_C: "72109-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale - LOINC 93245-9 ✅ VALIDATED */ C_SSRS: "93245-9",
    /** GAD-7 Anxiety Assessment Panel - LOINC 69737-5 ✅ VALIDATED */ GAD7: "69737-5",
    /** PC-PTSD-5 PTSD Screening Panel - LOINC 102010-6 ✅ VALIDATED */ PC_PTSD_5: "102010-6",
    /** PHQ-9 Depression Assessment Panel - LOINC 44249-1 ✅ VALIDATED */ PHQ9: "44249-1",
    /** PHQ-9A (Adolescent) Depression Assessment Panel - LOINC 89206-7 ✅ VALIDATED */ PHQ9A: "89206-7",
    /** PHQ-4 Combined Depression/Anxiety Panel - LOINC 69724-3 ✅ VALIDATED */ PHQ4: "69724-3",
    /** Health Leads Screening Panel (SDOH) - LOINC 99549-8 ✅ VALIDATED */ SDOH: "99549-8"
};
/**
 * Common questionnaire URLs used in FHIR Questionnaire.url
 * Uses official LOINC canonical URLs where available, organizational URLs otherwise
 */ const QUESTIONNAIRE_URLS = {
    ASRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.ASRS}`,
    C_SSRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.C_SSRS}`,
    GAD7: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.GAD7}`,
    OCI_4: "https://benchmarkhealth.org/fhir/Questionnaire/oci-4",
    PC_PTSD_5: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PC_PTSD_5}`,
    PSC_17: "https://benchmarkhealth.org/fhir/Questionnaire/psc-17",
    PHQ9: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9}`,
    PHQ9A: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9A}`,
    PHQ4: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ4}`,
    SBIRT_ED: "https://benchmarkhealth.org/fhir/Questionnaire/sbirt-ed",
    SAFETY_PLAN: "https://benchmarkhealth.org/fhir/Questionnaire/safety-plan",
    TAPS_1: "https://benchmarkhealth.org/fhir/Questionnaire/taps-1",
    TAPS_2: "https://benchmarkhealth.org/fhir/Questionnaire/taps-2",
    WPAI: "https://benchmarkhealth.org/fhir/Questionnaire/wpai-functional",
    WHO5: "https://benchmarkhealth.org/fhir/Questionnaire/who-5",
    SDOH: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.SDOH}`,
    MIRAH_COMPLEXITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-complexity",
    MIRAH_SEVERITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-severity",
    NPS: "https://benchmarkhealth.org/fhir/Questionnaire/nps-member-feedback",
    DRUG_USE: "https://benchmarkhealth.org/fhir/Questionnaire/drug-use-screen",
    GAD2: "https://benchmarkhealth.org/fhir/Questionnaire/gad-2",
    AUDIT_C: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.AUDIT_C}`,
    UCLA3: "https://benchmarkhealth.org/fhir/Questionnaire/ucla-3",
    PGI_S: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-s",
    PGI_C: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-c",
    WSAS: "https://benchmarkhealth.org/fhir/Questionnaire/wsas"
};

/**
 * The full nine-section order (§8.2). Suicide-risk (3) and safety-plan (4) are
 * trigger-only and excluded from base composition; they are exported separately.
 */ const SECTION_SPECS = [
    {
        key: "patient-rating",
        order: 1,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "PGI_S"
        ]
    },
    {
        key: "depression",
        order: 2,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "PHQ9"
        ]
    },
    {
        key: "anxiety",
        order: 5,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "GAD2",
            "GAD7"
        ]
    },
    {
        key: "loneliness",
        order: 6,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "UCLA3"
        ]
    },
    {
        key: "alcohol",
        order: 7,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "AUDIT_C"
        ]
    },
    {
        key: "drug-use",
        order: 8,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "DRUG_USE"
        ]
    },
    {
        key: "functioning",
        order: 9,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "WSAS"
        ]
    }
];
/**
 * The trigger-added sections (PRD §8.4 / D9), in order, inserted live by the UI
 * directly after depression on a positive self-harm answer — never carried by
 * memory (suicide-risk is re-derived each visit, E6).
 */ const TRIGGER_ADDED_SECTIONS = [
    {
        key: "suicide-risk",
        order: 3,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now by the depression self-harm answer",
        instruments: [
            "C_SSRS"
        ]
    },
    {
        key: "safety-plan",
        order: 4,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now — always paired with suicide risk",
        instruments: [
            "SAFETY_PLAN"
        ]
    }
];
/**
 * Every instrument that can appear in an adaptive-screener sitting, in actual
 * administration order. The list is derived from the section definitions so
 * timeline/read-back surfaces cannot drift from screener composition. PGI-S
 * and PGI-C occupy the same final section and are mutually exclusive per visit.
 */ const ADAPTIVE_SCREENER_INSTRUMENT_ORDER = [
    ...SECTION_SPECS,
    ...TRIGGER_ADDED_SECTIONS
].sort((a, b)=>a.order - b.order).flatMap((section)=>section.key === "patient-rating" ? [
        "PGI_S",
        "PGI_C"
    ] : section.instruments);
/** Canonical URLs matching `ADAPTIVE_SCREENER_INSTRUMENT_ORDER`. */ const ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER = ADAPTIVE_SCREENER_INSTRUMENT_ORDER.map((instrument)=>QUESTIONNAIRE_URLS[instrument]);
const ADAPTIVE_SCREENER_ORDER_BY_URL = new Map(ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER.map((url, index)=>[
        url,
        index
    ]));
/**
 * Return adaptive-screener result-like items in administration order without
 * mutating the input. Unknown URLs remain visible after known instruments and
 * preserve their incoming order.
 */ const sortByAdaptiveScreenerAdministrationOrder = (items)=>items.map((item, index)=>({
            item,
            index,
            order: ADAPTIVE_SCREENER_ORDER_BY_URL.get(item.questionnaireUrl ?? "") ?? Number.MAX_SAFE_INTEGER
        })).sort((a, b)=>a.order - b.order || a.index - b.index).map(({ item })=>item);

/**
 * Whether a status is an explicit advocate/patient decision — a patient decline
 * or the advocate's not-clinically-appropriate judgement — as opposed to the
 * `answered` status (re-derived from responses) or the transient `pending`
 * default. Only these survive the rail remounting (e.g. toggling the screener
 * off and back on) and must be restored as settlements; `answered` and `pending`
 * are always derived from the live responses, never rehydrated as a settlement,
 * so a not-yet-settled section can always still be declined after a remount.
 */ const isExplicitSettlement = (status)=>status === "declined-remaining" || status === "declined-section" || status === "not-administered-clinical";

/**
 * Reverse mapping from LOINC code to questionnaire URL.
 * @agentic @benchmark/core
 */ const getQuestionnaireUrlFromLoincCode = (loincCode)=>{
    for (const config of Object.values(STANDARD_QUESTIONNAIRES)){
        if (config.totalScoreLoincCode === loincCode) {
            return config.url;
        }
    }
    return null;
};
// =============================================================================
// SPECIALIZED SCORING METHODS
// =============================================================================
/**
 * Calculates WPAI score based on percentage-based impairment (not simple summation).
 * @agentic @benchmark/core
 */ const calculateWPAIScore = (responses)=>{
    // Get individual response values
    const getResponse = (linkId)=>responses.find((r)=>r.linkId === linkId)?.value ?? 0;
    const employed = getResponse("wpai-1"); // 0 = No, 1 = Yes
    const hoursHealthMissed = getResponse("wpai-2"); // 0-6 scale (0=0hrs, 6=40+ hrs)
    const hoursWorked = getResponse("wpai-4"); // 0-5 scale (0=0hrs, 5=40+ hrs)
    const workProductivity = getResponse("wpai-5"); // 0-10 scale
    const activityImpairment = getResponse("wpai-6"); // 0-10 scale
    // Convert scales to approximate hours for calculation
    const healthMissedHours = [
        0,
        4.5,
        12.5,
        20.5,
        28.5,
        36.5,
        45
    ][hoursHealthMissed] || 0;
    const actualWorkedHours = [
        0,
        5.5,
        15.5,
        25.5,
        35.5,
        45
    ][hoursWorked] || 0;
    let maxImpairment = 0;
    // Calculate Activity Impairment (always calculated)
    const activityImpairmentPercent = activityImpairment / 10 * 100;
    maxImpairment = Math.max(maxImpairment, activityImpairmentPercent);
    // Calculate work-related metrics only if employed
    if (employed === 1) {
        // Calculate Absenteeism (percentage of work time missed due to health)
        let absenteeismPercent = 0;
        if (healthMissedHours > 0 && healthMissedHours + actualWorkedHours > 0) {
            absenteeismPercent = healthMissedHours / (healthMissedHours + actualWorkedHours) * 100;
        }
        // Calculate Presenteeism (impairment while working) only if actually worked
        const presenteeismPercent = actualWorkedHours > 0 ? workProductivity / 10 * 100 : 0;
        // Calculate Overall Work Productivity Loss
        const workProductivityLossPercent = absenteeismPercent + presenteeismPercent * (1 - absenteeismPercent / 100);
        // Use the highest impairment percentage as the overall score
        if (presenteeismPercent > maxImpairment) {
            maxImpairment = presenteeismPercent;
        }
        if (workProductivityLossPercent > maxImpairment) {
            maxImpairment = workProductivityLossPercent;
        }
    }
    // Find the appropriate range for the maximum impairment percentage. Absenteeism
    // and overall work-productivity loss are fractional (derived from the WPAI hour
    // approximations), so match the range against the same rounded score we report:
    // matching the raw fraction against integer band boundaries leaves gaps
    // (e.g. 50.5 falls between the 26–50 and 51–75 bands and would misreport as
    // "Unable to determine" for a valid response).
    const roundedImpairment = Math.round(maxImpairment);
    const ranges = [
        {
            min: 0,
            max: 25,
            level: "minimal",
            interpretation: "Minimal impairment"
        },
        {
            min: 26,
            max: 50,
            level: "mild",
            interpretation: "Mild impairment"
        },
        {
            min: 51,
            max: 75,
            level: "moderate",
            interpretation: "Moderate impairment"
        },
        {
            min: 76,
            max: 100,
            level: "severe",
            interpretation: "Severe impairment"
        }
    ];
    const range = ranges.find((r)=>roundedImpairment >= r.min && roundedImpairment <= r.max);
    return {
        score: roundedImpairment,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level || "unknown",
        riskColor: range?.level === "severe" ? "red" : range?.level === "moderate" ? "orange" : range?.level === "mild" ? "yellow" : "gray"
    };
};
/**
 * Calculates C-SSRS score from ordinal severity levels (highest level reached).
 * @agentic @benchmark/core
 */ const calculateCSSRSScore = (responses, scoring)=>{
    // C-SSRS certified branching: Q1 (wish to be dead) and Q2 (active thoughts)
    // are always asked. Q3-Q5 are asked ONLY if Q2 is Yes. `gateFor` hides Q3-Q5
    // in the UI when Q2 is No, but stale answers can still survive on the saved
    // response, so scoring must enforce the gate itself — never derive a level
    // from Q3-Q5 unless Q2 is positive.
    const answerFor = (linkId)=>responses.find((r)=>r.linkId === linkId)?.value || 0;
    let highestLevel = 0;
    if (answerFor("c-ssrs-ideation-1") > 0) {
        highestLevel = 1;
    }
    const activeIdeation = answerFor("c-ssrs-ideation-2") > 0;
    if (activeIdeation) {
        highestLevel = 2;
        // Q3-Q5 only count when Q2 (active ideation) is positive.
        if (answerFor("c-ssrs-ideation-3") > 0) {
            highestLevel = 3;
        }
        if (answerFor("c-ssrs-ideation-4") > 0) {
            highestLevel = 4;
        }
        if (answerFor("c-ssrs-ideation-5") > 0) {
            highestLevel = 5;
        }
    }
    // Suicidal behavior raises risk regardless of ideation level, per the
    // certified Columbia triage: lifetime behavior floors at Moderate (level 3),
    // behavior within the past 3 months floors at High (level 4).
    const hasLifetimeBehavior = answerFor("c-ssrs-behavior-1") > 0;
    if (hasLifetimeBehavior && highestLevel < 3) {
        highestLevel = 3;
    }
    // Recency is only asked after lifetime behavior is Yes (certified branching).
    // A recency answer without positive lifetime behavior is stale/contradictory
    // data and must NOT raise risk.
    if (hasLifetimeBehavior && answerFor("c-ssrs-behavior-recency") > 0 && highestLevel < 4) {
        highestLevel = 4;
    }
    const severityLevels = scoring.severityLevels;
    const severityLevel = severityLevels?.find((level)=>level.level === highestLevel);
    const riskLevelValue = severityLevel?.riskLevel?.value || (highestLevel === 0 ? "minimal" : "unknown");
    return {
        score: highestLevel,
        interpretation: severityLevel?.interpretation || (highestLevel === 0 ? "No suicidal ideation" : "Unable to determine"),
        riskLevel: riskLevelValue,
        // Tier-appropriate color (D5): never signal "green/normal" for active
        // suicidal ideation. Overrides the shared risk-level palette where its
        // color (e.g. "low" → green) would under-signal C-SSRS urgency.
        riskColor: cssrsRiskColor(riskLevelValue)
    };
};
/**
 * Maps a C-SSRS risk-tier value to a display color reflecting triage urgency.
 * @agentic @benchmark/core
 */ const cssrsRiskColor = (riskLevelValue)=>{
    switch(riskLevelValue){
        case "minimal":
            return "green";
        case "low":
            return "yellow";
        case "moderate":
            return "orange";
        case "high":
            return "red";
        default:
            return "gray";
    }
};
/**
 * Calculates WSAS total with work-item N/A rescale (PRD §8.8).
 * @agentic @benchmark/core
 */ const calculateWSASScore = (responses, scoring)=>{
    const WORK_LINK_ID = "wsas-1";
    const workValue = responses.find((r)=>r.linkId === WORK_LINK_ID)?.value;
    const workNotApplicable = workValue === WSAS_WORK_NOT_APPLICABLE_VALUE;
    let sum = 0;
    let scoredCount = 0;
    for (const linkId of scoring.linkIds){
        const value = responses.find((r)=>r.linkId === linkId)?.value;
        if (value === undefined || value < 0) {
            continue;
        }
        if (linkId === WORK_LINK_ID && workNotApplicable) {
            continue;
        }
        sum += value;
        scoredCount += 1;
    }
    let totalScore = sum;
    if (workNotApplicable && scoredCount === 4) {
        totalScore = Math.round(sum * (5 / 4));
    }
    const range = scoring.ranges?.find((r)=>totalScore >= r.min && totalScore <= r.max);
    return {
        score: totalScore,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};
/**
 * Calculates ASRS score: sum Part A items (0-4 each), cutpoint ≥14.
 * @agentic @benchmark/core
 */ const calculateASRSScore = (responses, scoring)=>{
    const partALinkIds = scoring.partA?.linkIds ?? [];
    let partASum = 0;
    for (const linkId of partALinkIds){
        const response = responses.find((r)=>r.linkId === linkId);
        partASum += response?.value ?? 0;
    }
    const range = scoring.ranges?.find((r)=>partASum >= r.min && partASum <= r.max);
    return {
        score: partASum,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};
/**
 * Calculates questionnaire score from responses and scoring config; handles WPAI, C-SSRS, ASRS, summation.
 * @agentic @benchmark/core
 */ const calculateQuestionnaireScore = (responses, scoring)=>{
    // Strongly typed validation using type guard
    if (!isValidQuestionnaireScoring(scoring)) {
        console.error("Invalid scoring configuration: failed validation", {
            scoring
        });
        return {
            score: 0,
            interpretation: "Unable to determine: Invalid scoring configuration.",
            riskLevel: "unknown",
            riskColor: "gray"
        };
    }
    // Handle special scoring methods
    if (scoring.method === "wpai") {
        return calculateWPAIScore(responses);
    }
    if (scoring.method === "c-ssrs-ordinal") {
        return calculateCSSRSScore(responses, scoring);
    }
    if (scoring.method === "asrs-threshold") {
        return calculateASRSScore(responses, scoring);
    }
    if (scoring.method === "wsas-summation") {
        return calculateWSASScore(responses, scoring);
    }
    // Default summation method
    // NOTE: Callers are responsible for filtering out non-scorable responses
    // (items with scorable: false) before calling this function
    const totalScore = responses.reduce((sum, response)=>{
        return sum + (response.value || 0);
    }, 0);
    // Find the appropriate range for the score
    const range = scoring.ranges?.find((r)=>totalScore >= r.min && totalScore <= r.max);
    return {
        score: totalScore,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};

/**
 * Group results carrying the same durable adaptive-screener session id and
 * order each sitting by administration sequence. Untagged/manual assessments
 * are returned unchanged and in their incoming order.
 */ const groupQuestionnaireResultsByAdaptiveSession = (results)=>{
    const groups = new Map();
    const standalone = [];
    for (const result of results){
        const marker = result.adaptiveScreener;
        if (!marker) {
            standalone.push(result);
            continue;
        }
        const group = groups.get(marker.sessionId) ?? {
            sessionId: marker.sessionId,
            baseline: marker.baseline,
            results: []
        };
        group.results.push(result);
        groups.set(marker.sessionId, group);
    }
    const adaptiveGroups = [
        ...groups.values()
    ].map((group)=>{
        const orderedResults = sortByAdaptiveScreenerAdministrationOrder(group.results);
        const authored = orderedResults.reduce((latest, result)=>result.authored && (!latest || result.authored > latest) ? result.authored : latest, undefined);
        return {
            ...group,
            authored,
            results: orderedResults
        };
    });
    return {
        adaptiveGroups,
        standalone
    };
};
/**
 * Risk levels that flag a result as high-acuity by severity band.
 * @agentic @benchmark/core
 */ const HIGH_ACUITY_RISK_LEVELS = new Set([
    "moderately-severe",
    "severe",
    "high"
]);
/** Whether a score Observation is active clinical data for display/linking. */ const isActiveAssessmentObservation = (observation)=>observation.status !== "cancelled" && observation.status !== "entered-in-error";
/**
 * Per-questionnaire rules that mark a result as a safety concern (e.g. suicidality).
 * PHQ-9/PHQ-9A item 9 > 0 mirrors the in-encounter C-SSRS recommendation; C-SSRS uses its
 * ordinal score (>= 3 = active ideation with method/intent or any suicidal behavior).
 * @agentic @benchmark/core
 */ const SAFETY_SIGNAL_RULES = {
    [QUESTIONNAIRE_URLS$1.PHQ9]: {
        itemSignals: [
            {
                linkId: "phq9-9",
                minValue: 1
            }
        ]
    },
    [QUESTIONNAIRE_URLS$1.PHQ9A]: {
        itemSignals: [
            {
                linkId: "phq9a-9",
                minValue: 1
            }
        ]
    },
    [QUESTIONNAIRE_URLS$1.C_SSRS]: {
        minScore: 3
    }
};
/**
 * Pure acute-risk trigger evaluation for the clinical notification system.
 *
 * Reuses the display-time safety thresholds (PHQ-9/PHQ-9A item-9 >= 1,
 * C-SSRS >= 3) and high-acuity risk bands. Pure and IO-free so it can run in
 * `apps/api` or a Medplum Bot. Note the intentional PHQ-9 item-9 threshold
 * (>= 1) is authoritative and stricter than the members crisis banner (D10).
 * @agentic @benchmark/core
 */ const deriveNotificationRiskSignal = (input)=>{
    const { questionnaireUrl: url, responses, scoreValue, riskLevel, scoring } = input;
    if (url) {
        const rule = SAFETY_SIGNAL_RULES[url];
        if (rule) {
            if (rule.itemSignals) {
                for (const signal of rule.itemSignals){
                    const response = responses.find((r)=>r.linkId === signal.linkId);
                    if (response && response.value >= signal.minValue) {
                        return {
                            isAcuteRisk: true,
                            reason: "safety"
                        };
                    }
                }
            }
            if (rule.minScore !== undefined) {
                // Prefer the gated authoritative score (applies certified branching such
                // as C-SSRS gating); fall back to the caller's scoreValue only when no
                // scoring config is available to recompute it.
                const gatedScore = scoring ? calculateQuestionnaireScore(responses.map((r)=>({
                        linkId: r.linkId,
                        value: r.value
                    })), scoring).score : scoreValue;
                if (gatedScore !== undefined && gatedScore >= rule.minScore) {
                    return {
                        isAcuteRisk: true,
                        reason: "safety"
                    };
                }
            }
        }
    }
    if (riskLevel && HIGH_ACUITY_RISK_LEVELS.has(riskLevel)) {
        return {
            isAcuteRisk: true,
            reason: "severity"
        };
    }
    return {
        isAcuteRisk: false
    };
};
const buildQuestionMap = (questions, map = new Map())=>{
    for (const question of questions ?? []){
        if (question.item?.length) {
            buildQuestionMap(question.item, map);
        }
        const displayByValue = new Map();
        const displayByCode = new Map();
        const valueByCode = new Map();
        for (const opt of question.answerOptions ?? []){
            displayByValue.set(opt.value, opt.display);
            displayByCode.set(opt.code, opt.display);
            valueByCode.set(opt.code, opt.value);
        }
        for (const opt of question.options ?? []){
            if (!displayByValue.has(opt.value)) {
                displayByValue.set(opt.value, opt.display);
            }
        }
        map.set(question.linkId, {
            text: question.text ?? question.linkId,
            displayByValue,
            displayByCode,
            valueByCode
        });
    }
    return map;
};
const orderedConfigLinkIds = (questions, acc = [])=>{
    for (const question of questions ?? []){
        if (question.item?.length) {
            orderedConfigLinkIds(question.item, acc);
        } else {
            acc.push(question.linkId);
        }
    }
    return acc;
};
const collectRawAnswers = (items, acc = new Map())=>{
    for (const item of items ?? []){
        if (item.item?.length) {
            collectRawAnswers(item.item, acc);
        }
        const answer = item.answer?.[0];
        if (answer) {
            acc.set(item.linkId, {
                linkId: item.linkId,
                text: item.text,
                valueInteger: answer.valueInteger,
                valueString: answer.valueString,
                codingCode: answer.valueCoding?.code,
                codingDisplay: answer.valueCoding?.display
            });
        }
    }
    return acc;
};
const resolveNumericValue = (raw, question)=>{
    if (raw.valueInteger !== undefined) return raw.valueInteger;
    if (raw.codingCode) {
        const byCode = question?.valueByCode.get(raw.codingCode);
        if (byCode !== undefined) return byCode;
        // Surveys served from local questionnaire constants (e.g. the members app)
        // encode the Likert weight directly as the answer code ("0"–"3") rather than
        // a LOINC code, so fall back to the integer code when it isn't in the config.
        if (/^-?\d+$/.test(raw.codingCode)) {
            return Number(raw.codingCode);
        }
    }
    return undefined;
};
const resolveAnswerLabel = (raw, question, numericValue)=>{
    if (raw.valueString !== undefined && raw.valueString !== "") {
        return raw.valueString;
    }
    if (raw.codingDisplay) return raw.codingDisplay;
    if (raw.codingCode) {
        const codeDisplay = question?.displayByCode.get(raw.codingCode);
        if (codeDisplay) return codeDisplay;
    }
    if (numericValue !== undefined) {
        return question?.displayByValue.get(numericValue) ?? String(numericValue);
    }
    return raw.codingCode ?? "—";
};
const deriveAuthorType = (qr)=>{
    const source = qr.meta?.source;
    if (source === "members") return "member";
    if (source === "mirah") return "imported";
    if (qr.author) return "clinician";
    return "unknown";
};
const isSafetySignal = (url, responses, score)=>{
    if (!url) return false;
    const rule = SAFETY_SIGNAL_RULES[url];
    if (!rule) return false;
    if (rule.itemSignals) {
        for (const signal of rule.itemSignals){
            const response = responses.find((r)=>r.linkId === signal.linkId);
            if (response && response.value >= signal.minValue) return true;
        }
    }
    if (rule.minScore !== undefined && score && score.value >= rule.minScore) {
        return true;
    }
    return false;
};
const deriveAcuity = (url, responses, score, safetyOnly = false)=>{
    if (isSafetySignal(url, responses, score)) {
        return {
            level: "safety",
            label: "Safety concern",
            color: "red"
        };
    }
    if (!safetyOnly && score && HIGH_ACUITY_RISK_LEVELS.has(score.riskLevel)) {
        return {
            level: "severity",
            label: score.interpretation,
            color: score.severityColor || "red"
        };
    }
    return null;
};
/**
 * Derives the short display label for a risk level value from the canonical
 * OBSERVATION_RISK_LEVELS constants (e.g. "moderate" → "Moderate").
 */ const getRiskLevelDisplay = (riskLevelValue)=>{
    const entry = Object.values(OBSERVATION_RISK_LEVELS).find((level)=>level.value === riskLevelValue);
    return entry?.display ?? riskLevelValue;
};
const computeScore = (url, config, responses)=>{
    if (!url || !config?.scoring || config.scoring.linkIds.length === 0) {
        return undefined;
    }
    // Sum only the instrument's scorable items, identified by the scoring
    // config's linkId allow-list. Using an allow-list (rather than a
    // non-scorable blacklist) guarantees that neither non-scorable items (e.g.
    // the PHQ-9 functional-impairment question) nor unrecognized/foreign
    // linkIds — such as the source-specific mnemonics on Mirah-imported
    // responses — can ever be added into the total.
    const scorableLinkIds = new Set(config.scoring.linkIds);
    const scorable = responses.filter((r)=>scorableLinkIds.has(r.linkId));
    if (scorable.length === 0) return undefined;
    const result = calculateQuestionnaireScore(scorable, config.scoring);
    return {
        value: result.score,
        maxScore: config.scoring.maxScore ?? 0,
        interpretation: result.interpretation,
        riskLevel: result.riskLevel,
        riskLevelDisplay: getRiskLevelDisplay(result.riskLevel),
        severityColor: result.riskColor ?? "gray"
    };
};
/**
 * Reverse-derive a display score from a persisted total. Used both by
 * standalone score Observations and as the authoritative fallback for
 * imported responses whose item linkIds don't align with the scoring config.
 */ const severityFromTotal = (url, value)=>{
    const config = getEnhancedQuestionnaireConfig(url);
    const ranges = config?.scoring?.ranges;
    if (!ranges) return undefined;
    const range = ranges.find((r)=>value >= r.min && value <= r.max);
    if (!range) return undefined;
    return {
        value,
        maxScore: config?.scoring?.maxScore ?? 0,
        interpretation: range.interpretation,
        riskLevel: range.level.value,
        riskLevelDisplay: range.level.display,
        severityColor: range.level.color
    };
};
/**
 * Score sourced from a linked persisted total-score Observation. Falls back to
 * a bare value (no severity) when the total doesn't map to a known band.
 */ const scoreFromLinkedObservation = (url, observation, config)=>{
    const value = observation.valueInteger;
    if (value === undefined) return undefined;
    const banded = url ? severityFromTotal(url, value) : undefined;
    return banded ?? {
        value,
        maxScore: config?.scoring?.maxScore ?? 0,
        interpretation: "",
        riskLevel: "unknown",
        riskLevelDisplay: "Unknown",
        severityColor: "gray"
    };
};
/** Bundle-local placeholder prefix the Mirah CSV importer uses for QR references. */ const MIRAH_QR_PLACEHOLDER_PREFIX = "urn:uuid:qr-";
const QUESTIONNAIRE_RESPONSE_REFERENCE_PREFIX = "QuestionnaireResponse/";
/** Identifier system the Mirah CSV importer stamps on imported resources. */ const MIRAH_IDENTIFIER_SYSTEM = "https://mirah.com";
const questionnaireResponseIdentifiers = (qr)=>{
    const identifier = qr.identifier;
    if (!identifier) return [];
    // FHIR types QuestionnaireResponse.identifier as a single object, but be
    // defensive: imported/legacy data has been seen with both shapes.
    return (Array.isArray(identifier) ? identifier : [
        identifier
    ]).filter(Boolean);
};
/**
 * Index total-score Observations by the id of the QuestionnaireResponse they
 * belong to, so QR-based views (assessments tab, timeline) can resolve an
 * authoritative total when the response items don't align with the scoring
 * config (imported data).
 *
 * Resolves two reference shapes on `Observation.derivedFrom`:
 *  1. `QuestionnaireResponse/{id}` — the standard resolved reference.
 *  2. `urn:uuid:qr-<composite>` — an *unresolved* bundle-local placeholder left
 *     by the Mirah CSV importer (Medplum did not rewrite it to a real id). The
 *     placeholder embeds the QR's composite `identifier.value` (with `|`
 *     replaced by `-`), so it maps back to the QR deterministically:
 *     `urn:uuid:qr-${qr.identifier.value.replace(/\|/g, "-")}`.
 *
 * Placeholder keys are built only from `https://mirah.com` identifiers so a
 * non-Mirah QR whose identifier happens to normalize to the same token can
 * never be bound to an imported score Observation.
 *
 * Only Observations carrying a numeric total (`valueInteger`) are indexed; the
 * first (caller sorts newest-first) score per QR wins.
 * @agentic @benchmark/core
 */ const buildScoreObservationByResponseId = (responses, observations)=>{
    const qrIdByPlaceholder = new Map();
    for (const qr of responses){
        if (!qr.id) continue;
        for (const identifier of questionnaireResponseIdentifiers(qr)){
            const value = identifier.value;
            if (!value || identifier.system !== MIRAH_IDENTIFIER_SYSTEM) continue;
            const placeholder = `${MIRAH_QR_PLACEHOLDER_PREFIX}${value.replace(/\|/g, "-")}`;
            if (!qrIdByPlaceholder.has(placeholder)) {
                qrIdByPlaceholder.set(placeholder, qr.id);
            }
        }
    }
    const byQrId = new Map();
    for (const observation of observations){
        if (!isActiveAssessmentObservation(observation)) continue;
        if (observation.valueInteger === undefined) continue;
        for (const ref of observation.derivedFrom ?? []){
            const reference = ref.reference;
            if (!reference) continue;
            let qrId;
            if (reference.startsWith(QUESTIONNAIRE_RESPONSE_REFERENCE_PREFIX)) {
                qrId = reference.slice(QUESTIONNAIRE_RESPONSE_REFERENCE_PREFIX.length);
            } else if (reference.startsWith(MIRAH_QR_PLACEHOLDER_PREFIX)) {
                qrId = qrIdByPlaceholder.get(reference);
            }
            if (qrId && !byQrId.has(qrId)) byQrId.set(qrId, observation);
        }
    }
    return byQrId;
};
/**
 * Derives a display-ready result view from a QuestionnaireResponse. Score, severity, and acuity
 * are computed from the response items via existing scoring config; nothing is persisted.
 * @agentic @benchmark/core
 */ const deriveQuestionnaireResult = (qr, options)=>{
    const url = qr.questionnaire;
    const config = url ? getEnhancedQuestionnaireConfig(url) : null;
    const questionMap = buildQuestionMap(config?.questions);
    const rawAnswers = collectRawAnswers(qr.item);
    const marker = readAdaptiveScreenerMarker(qr);
    const skippedIds = new Set(marker?.skippedLinkIds ?? []);
    const answers = [];
    const scorableResponses = [];
    const consumed = new Set();
    const appendAnswer = (raw)=>{
        const question = questionMap.get(raw.linkId);
        const numericValue = resolveNumericValue(raw, question);
        const answerLabel = resolveAnswerLabel(raw, question, numericValue);
        answers.push({
            linkId: raw.linkId,
            questionText: (marker ? raw.text ?? question?.text : question?.text ?? raw.text) ?? raw.linkId,
            answerLabel,
            rawValue: numericValue ?? raw.valueString
        });
        if (numericValue !== undefined) {
            scorableResponses.push({
                linkId: raw.linkId,
                value: numericValue
            });
        }
    };
    const appendSkipped = (linkId)=>{
        answers.push({
            linkId,
            questionText: questionMap.get(linkId)?.text ?? "Question wording unavailable",
            answerLabel: "Skipped",
            skipped: true
        });
        consumed.add(linkId);
    };
    // Prefer config question order; fall back to the response's own item order.
    for (const linkId of orderedConfigLinkIds(config?.questions)){
        const raw = rawAnswers.get(linkId);
        if (raw) {
            appendAnswer(raw);
            consumed.add(linkId);
        } else if (skippedIds.has(linkId)) {
            appendSkipped(linkId);
        }
    }
    for (const [linkId, raw] of rawAnswers){
        if (!consumed.has(linkId)) {
            appendAnswer(raw);
            consumed.add(linkId);
        }
    }
    for (const linkId of skippedIds){
        if (!consumed.has(linkId)) appendSkipped(linkId);
    }
    // Prefer the item-level recompute (which also powers item-based safety
    // signals). When the response items don't align with the scoring config —
    // e.g. Mirah-imported responses whose linkIds are source-specific mnemonics
    // — the recompute yields no aligned score; fall back to the authoritative
    // persisted total-score Observation, whose value already excludes
    // non-scorable items such as the PHQ-9 functional-impairment question.
    const computedScore = computeScore(url, config, scorableResponses) ?? (options?.linkedScoreObservation ? scoreFromLinkedObservation(url, options.linkedScoreObservation, config) : undefined);
    // An adaptive-screener section that settled as a decline keeps its partial
    // answers, but a part-answered set is not a valid score (D8): suppress the
    // computed total and its severity band. Item-level safety signals (and the
    // C-SSRS gated minimum-score rule) still fire from whatever WAS answered —
    // a positive self-harm answer must never be hidden by a decline.
    const adaptiveSettlement = marker?.settlement && isExplicitSettlement(marker.settlement) ? marker.settlement : undefined;
    const completion = url ? evaluateQuestionnaireCompletion(url, [
        ...rawAnswers.values()
    ].map((answer)=>({
            linkId: answer.linkId,
            value: answer.valueInteger,
            valueString: answer.valueString,
            coding: answer.codingCode !== undefined ? {
                code: answer.codingCode
            } : undefined
        })), {
        declaredComplete: qr.status === "completed"
    }) : undefined;
    const supportedIncomplete = completion?.supported === true && !completion.complete;
    // The patient decline path persists explicitly settled adaptive sections as
    // `stopped`; those are recorded outcomes whose safety signal must survive.
    const inactiveResponse = qr.status === "entered-in-error" || qr.status === "stopped" && !adaptiveSettlement;
    const score = adaptiveSettlement || supportedIncomplete || inactiveResponse ? undefined : computedScore;
    const acuity = inactiveResponse ? null : deriveAcuity(url, scorableResponses, computedScore, Boolean(adaptiveSettlement || supportedIncomplete));
    return {
        id: qr.id ?? "",
        source: "questionnaire",
        questionnaireUrl: url,
        name: config?.name ?? getQuestionnaireResponseDisplayName(qr),
        title: config?.title,
        authored: qr.authored,
        status: qr.status,
        authorType: deriveAuthorType(qr),
        authorDisplay: qr.author?.display ?? undefined,
        score,
        acuity,
        answers,
        adaptiveSettlement,
        ...marker ? {
            adaptiveScreener: marker
        } : {}
    };
};
const deriveObservationAuthorType = (observation)=>{
    const source = observation.meta?.source;
    if (source === "members") return "member";
    if (source === "mirah") return "imported";
    if (observation.performer && observation.performer.length > 0) {
        return "clinician";
    }
    return "unknown";
};
/**
 * Derives a result view from a standalone, manually-logged survey Observation (no linked
 * QuestionnaireResponse). Severity is reverse-looked-up from the score; there are no item
 * answers, so safety-signal acuity does not apply.
 * @agentic @benchmark/core
 */ const deriveObservationResult = (observation)=>{
    const code = observation.code?.coding?.[0]?.code;
    const url = code ? getQuestionnaireUrlFromLoincCode(code) ?? undefined : undefined;
    const value = observation.valueInteger;
    let score;
    if (value !== undefined) {
        score = url !== undefined ? severityFromTotal(url, value) : undefined;
        score ??= {
            value,
            maxScore: 0,
            interpretation: "",
            riskLevel: "unknown",
            riskLevelDisplay: "Unknown",
            severityColor: "gray"
        };
    }
    const acuity = score && HIGH_ACUITY_RISK_LEVELS.has(score.riskLevel) ? {
        level: "severity",
        label: score.interpretation,
        color: score.severityColor || "red"
    } : null;
    const name = code ? getObservationDisplayByLoincCode(code) : observation.code?.text ?? "Assessment";
    return {
        id: observation.id ?? "",
        source: "score-entry",
        questionnaireUrl: url,
        name,
        authored: observation.effectiveDateTime ?? observation.issued,
        status: observation.status,
        authorType: deriveObservationAuthorType(observation),
        score,
        acuity,
        answers: []
    };
};

/** Lookup table for canonical activity order by v2 activity code. */ const ACTIVITY_ORDER_BY_CODE = new Map(getAllCarePlanActivityOptions().map((activity)=>[
        activity.code,
        activity.order
    ]));
/**
 * Returns task type display name from code or resource.
 * @agentic @benchmark/core
 */ const getTaskTypeDisplay = (task)=>{
    // Prefer the explicit display on the Task resource, if provided
    const display = task.code?.coding?.[0]?.display;
    if (display) {
        return display;
    }
    // Otherwise derive display from the task code via the constants helper
    const code = task.code?.coding?.[0]?.code;
    if (code) {
        return getTaskTypeDisplay$1(code);
    }
    return "Task";
};
/** Canonical FHIR/Medplum UUID resource id shape (8-4-4-4-12 hex). */ const FHIR_UUID_SOURCE = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
/**
 * Strips an `(encounter <uuid>)` parenthetical, and a bare `encounter <uuid>`
 * (keeping the word "encounter"). Both are scoped to the `encounter ` prefix
 * and require a strict UUID shape so unrelated context text that happens to
 * contain a UUID is never mutated.
 */ const ENCOUNTER_UUID_PARENTHETICAL = new RegExp(`\\(encounter ${FHIR_UUID_SOURCE}\\)`, "g");
const ENCOUNTER_UUID = new RegExp(`encounter ${FHIR_UUID_SOURCE}`, "g");
/**
 * Cleans a `Task.statusReason.text` string for display by stripping raw resource
 * UUIDs that older completion flows embedded into the human-readable context —
 * e.g. "Completed via encounter <uuid>" becomes "Completed via encounter" and
 * "... (encounter <uuid>)" drops the parenthetical entirely.
 *
 * New completions write a human-friendly label (see `getEncounterCompletionLabel`)
 * and contain no UUID, so this is a no-op for them; it exists to keep historical
 * task history entries readable without a data backfill.
 *
 * Regexes deliberately use a literal single space rather than `\s*`/`\s+`: the
 * stored strings only ever contain single ASCII spaces, and a leading unbounded
 * whitespace quantifier on a global replace re-scans at every position, which is
 * quadratic on adversarial input (CodeQL `js/polynomial-redos`).
 * @agentic @benchmark/core
 */ const formatStatusReasonForDisplay = (text)=>{
    if (!text) {
        return text;
    }
    return text.replace(ENCOUNTER_UUID_PARENTHETICAL, "").replace(ENCOUNTER_UUID, "encounter").replace(/ {2,}/g, " ").trim();
};
/**
 * Reads a Task's status reason text, cleaned for display via
 * {@link formatStatusReasonForDisplay}.
 * @agentic @benchmark/core
 */ const getTaskStatusReasonDisplay = (task)=>formatStatusReasonForDisplay(task.statusReason?.text);
/**
 * The exact top-level clause labels that completion flows emit (single source of
 * truth for both the clause split and keyed detection below). A clause is only
 * `keyed`, and the top-level split only fires, when a clause begins with one of
 * these labels followed by `": "`. This is what keeps free text intact: a note
 * such as `Notes: Discussed options. Recommendation: PHP` has no allow-listed
 * label after its internal `". "`, so it is not split, and a standalone
 * `Summary: …` note is not mistaken for a keyed segment.
 */ const STATUS_REASON_LABELS = [
    "Selected services",
    "Services",
    "Notes",
    "Approved",
    "Denied",
    "Needs more information",
    "Outcome",
    "Follow-up",
    "Member accepted",
    "Member declined",
    "Member wants changes",
    "Appointments scheduled",
    "CoCM care plan built with activities"
];
const STATUS_REASON_LABEL_SET = new Set(STATUS_REASON_LABELS);
/**
 * Splits a status-reason string into top-level clauses. Completion flows join
 * clauses with `". "` and every non-leading clause begins with one of
 * {@link STATUS_REASON_LABELS} + `": "`. Breaking only before an allow-listed
 * label keeps sentence-ending periods inside a clause value from fragmenting it
 * — e.g. the `Dr. Jane Doe` in a consult string, or a `". Recommendation: "`
 * inside a free-text note, stays within its clause.
 *
 * Built from the constant allow-list above (labels are escaped); a bounded
 * alternation of literals with a single lookahead — no nested quantifiers, so it
 * stays linear on adversarial input (CodeQL `js/polynomial-redos`).
 */ const STATUS_REASON_CLAUSE_SEPARATOR = new RegExp(`\\. +(?=(?:${STATUS_REASON_LABELS.map((label)=>label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}): )`);
/**
 * Parses one top-level clause into a segment. A clause is `keyed` only when the
 * text before its first `:` is an allow-listed label ({@link STATUS_REASON_LABELS});
 * the remainder is split into items on `;`. Anything else — including free-text
 * notes and non-allow-listed `Word: value` prose — stays a `text` segment. Uses
 * string operations (`indexOf`/`split`) to stay linear on adversarial input.
 */ const parseStatusReasonClause = (clause)=>{
    const colonIndex = clause.indexOf(":");
    if (colonIndex > 0) {
        const label = clause.slice(0, colonIndex).trim();
        if (STATUS_REASON_LABEL_SET.has(label)) {
            const rest = clause.slice(colonIndex + 1).trim();
            if (rest) {
                const items = rest.split(";").map((item)=>item.trim()).filter(Boolean);
                return {
                    kind: "keyed",
                    label,
                    items
                };
            }
        }
    }
    return {
        kind: "text",
        value: clause
    };
};
/**
 * Parses a completion status-reason string into labeled segments for structured
 * display. Completion flows assemble these strings with a stable grammar:
 * top-level clauses joined by `". "`, each clause `Label: value`, and multi-item
 * values joined by `"; "` (e.g. `Selected services: Individual Therapy: …;
 * Partial Hospitalization (PHP): …`). This is tolerant: only clauses led by an
 * allow-listed label ({@link STATUS_REASON_LABELS}) become `keyed`; everything
 * else — free-text notes, legacy strings, non-allow-listed `Word: value` prose —
 * becomes a `text` segment and renders unchanged rather than being mangled.
 *
 * Values are itemized on `";"` only. Comma-joined values (e.g. the CoCM
 * `built with activities: A (date), B (date)` summary) render as a single
 * labeled line rather than one line per item — commas appear inside legitimate
 * item text and free-text notes, so splitting on them would fragment those.
 *
 * `Notes` is the free-text terminal clause every emitting flow appends last (in
 * consult strings it is followed only by `Follow-up`); its prose can itself
 * contain `"<sentence>. <AllowlistedLabel>: …"`, which the clause split would
 * otherwise fragment. So any clauses that follow the first `Notes:` are re-joined
 * back into it, keeping the note intact. This does not fully disambiguate a
 * non-`Notes` value that embeds an allow-listed label, which remains a known
 * limitation of parsing an unstructured string.
 *
 * The input is expected to already be cleaned via
 * {@link formatStatusReasonForDisplay}.
 * @agentic @benchmark/core
 */ const parseStatusReason = (text)=>{
    const cleaned = text?.trim();
    if (!cleaned) return [];
    const clauses = cleaned.split(STATUS_REASON_CLAUSE_SEPARATOR).map((clause)=>clause.replace(/\.$/, "").trim()).filter(Boolean);
    const notesIndex = clauses.findIndex((clause)=>clause.startsWith("Notes:"));
    const clausesWithNotesIntact = notesIndex >= 0 ? [
        ...clauses.slice(0, notesIndex),
        clauses.slice(notesIndex).join(". ")
    ] : clauses;
    return clausesWithNotesIntact.map(parseStatusReasonClause);
};
/**
 * Returns task assignee name from owner or requester.
 * @agentic @benchmark/core
 */ const getTaskAssigneeName = (task)=>{
    if (task.owner?.display) {
        return task.owner.display;
    }
    if (task.requester?.display) {
        return task.requester.display;
    }
    return task.owner?.reference || task.requester?.reference || "Unknown";
};
/**
 * Returns task requester name (creator).
 * @agentic @benchmark/core
 */ const getTaskRequesterName = (task)=>{
    if (task.requester?.display) {
        return task.requester.display;
    }
    return null;
};
/**
 * Returns task due date from restriction.period.end.
 * @agentic @benchmark/core
 */ const getTaskDueDate = (task)=>task.restriction?.period?.end;
/**
 * Returns the display name used in completed-task timeline summaries.
 * @agentic @benchmark/core
 */ const getTaskCompletionTimelineActivityName = (task)=>getTaskTypeDisplay(task);
/**
 * Returns the care-plan activity order used for ordering collapsed completed tasks.
 * @agentic @benchmark/core
 */ const getTaskCompletionTimelineActivityOrder = (task)=>{
    const rawCode = task.code?.coding?.[0]?.code;
    if (!rawCode) {
        return Number.MAX_SAFE_INTEGER;
    }
    const canonicalCode = resolveActivityCode$1(rawCode);
    return ACTIVITY_ORDER_BY_CODE.get(canonicalCode) ?? Number.MAX_SAFE_INTEGER;
};
/**
 * Returns attribution display for completed tasks when a different user completed it.
 * @agentic @benchmark/core
 */ const getTaskCompletionAttributionDisplay = (task, currentUserRole)=>{
    const completionActor = task.owner ?? task.requester;
    if (!completionActor?.display) {
        return null;
    }
    const currentUserRoleRef = currentUserRole?.id ? `PractitionerRole/${currentUserRole.id}` : null;
    if (currentUserRoleRef && completionActor.reference === currentUserRoleRef) {
        return null;
    }
    return completionActor.display;
};
/**
 * Extracts the canonical v2 activity code from a Task's code.coding, filtering
 * by the care-plan activity system and normalizing legacy codes.
 * @agentic @benchmark/core
 */ const resolveTaskActivityCode = (task)=>{
    const rawCode = task.code?.coding?.find((c)=>c.system === CARE_PLAN_ACTIVITY_SYSTEM)?.code;
    if (!rawCode) return undefined;
    return resolveActivityCode$1(rawCode);
};
/**
 * Reads a Task's activity code via a `Task.basedOn`-style ref + lookup.
 * Used when a CarePlan.activity has only a `reference` (no detail) and the
 * caller has already loaded the linked Tasks. Unlike `resolveTaskActivityCode`,
 * this returns the raw `coding[0].code` without filtering by system or
 * normalizing legacy codes — preserves the call-site behavior of the
 * apps-side helper this replaces.
 * @agentic @benchmark/core
 */ const resolveTaskActivityCodeByRef = (taskRef, tasksById)=>{
    if (!taskRef) return undefined;
    const taskId = taskRef.startsWith("Task/") ? taskRef.slice(5) : taskRef;
    return tasksById.get(taskId)?.code?.coding?.[0]?.code;
};
/**
 * True when at least one Task in the list has activity code "offer-services"
 * and status "completed". Resource-agnostic — any caller passing Tasks for
 * any CarePlan can use this.
 * @agentic @benchmark/core
 */ const hasCompletedOfferServicesTask = (tasks)=>tasks.some((t)=>resolveTaskActivityCode(t) === "offer-services" && t.status === "completed");
/**
 * Returns the first ready/in-progress Task as `{ code, display, owner, dueDate }`.
 * Resource-agnostic (no triage assumption).
 * @agentic @benchmark/core
 */ const resolveNextReadyTaskActivity = (tasks)=>{
    const readyTask = tasks.find((t)=>t.status === "ready" || t.status === "in-progress");
    if (!readyTask) return undefined;
    const code = resolveTaskActivityCode(readyTask) ?? "unknown";
    return {
        code,
        display: getCarePlanActivityDisplay(code),
        owner: readyTask.owner?.display,
        dueDate: readyTask.restriction?.period?.end
    };
};
/**
 * Returns the saved draft form state on a Task, or `undefined` when there is
 * none or it is unparsable / not a JSON object (a corrupt draft never blocks
 * the task — the form seeds as if no draft existed).
 * @agentic @benchmark/core
 */ const getTaskDraftFormState = (task)=>{
    const raw = task?.extension?.find((ext)=>ext.url === TASK_DRAFT_FORM_STATE_EXTENSION_URL)?.valueString;
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : undefined;
    } catch  {
        return undefined;
    }
};
/**
 * Reads the CarePlan resource ID from a Task's basedOn list (Task → CarePlan link).
 * @agentic @benchmark/core
 */ const getCarePlanIdFromTask = (task)=>{
    if (!task) {
        return undefined;
    }
    const carePlanRef = task.basedOn?.find((ref)=>ref.reference?.startsWith("CarePlan/"));
    return carePlanRef?.reference ? getResourceIdFromReference(carePlanRef.reference, "CarePlan") : undefined;
};

/**
 * Computes days since ServiceRequest creation or since offer-services was
 * completed. When services have been offered, counts days since the
 * offer-services Task completion. Otherwise counts days since the earliest
 * ServiceRequest authoredOn (falling back to CarePlan dates).
 * @agentic @benchmark/core
 */ const resolveServiceRequestDaysWaiting = (carePlan, tasks, serviceRequestDate, now = new Date())=>{
    const offered = hasCompletedOfferServicesTask(tasks);
    if (offered) {
        const offerTask = tasks.find((t)=>resolveTaskActivityCode(t) === "offer-services" && t.status === "completed");
        const completedDate = offerTask?.meta?.lastUpdated;
        if (completedDate) {
            return daysBetween(new Date(completedDate), now);
        }
    }
    const createdDate = serviceRequestDate ?? carePlan.period?.start ?? carePlan.meta?.lastUpdated;
    if (!createdDate) return 0;
    return daysBetween(new Date(createdDate), now);
};
/** Whole-day difference between two dates. */ const daysBetween = (from, to)=>{
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.max(0, Math.floor((to.getTime() - from.getTime()) / msPerDay));
};
/**
 * Computes a display label for a ServiceRequest's service type.
 * @agentic @benchmark/core
 */ const getServiceTypeDisplay = (sr)=>sr.code?.coding?.[0]?.display || sr.code?.text || "Unknown Service";
/**
 * Extracts condition display labels from a ServiceRequest's reasonCode.
 * @agentic @benchmark/core
 */ const getConditionDisplays = (sr)=>sr.reasonCode?.map((rc)=>rc.coding?.[0]?.display).filter(Boolean) || [];
/** Prefixes used by system-generated annotations on ServiceRequest.note. */ const SYSTEM_NOTE_PREFIXES = [
    "Service Selection:",
    "Service Rejection:",
    "Service Approved:",
    "Member Response:",
    "Member Accepted:"
];
/**
 * Extracts member preferences text from notes, skipping system-generated
 * annotations.
 * @agentic @benchmark/core
 */ const getPreferencesText = (sr)=>sr.note?.find((n)=>!SYSTEM_NOTE_PREFIXES.some((p)=>n.text?.startsWith(p)))?.text;
/**
 * Returns true when a ServiceRequest has a selected HealthcareService performer.
 * @agentic @benchmark/core
 */ const hasSelectedService = (sr)=>Boolean(sr.performer?.some((p)=>p.reference?.startsWith("HealthcareService/")));
/**
 * Gets selected HealthcareService display from a ServiceRequest performer list.
 * @agentic @benchmark/core
 */ const getSelectedServiceDisplay = (sr)=>sr.performer?.find((p)=>p.reference?.startsWith("HealthcareService/"))?.display || "Unspecified Service";
/** Known prefixed segments in a "Service Selection:" note body. */ const SELECTION_SEGMENT_PREFIXES = [
    "ServiceName: ",
    "ServiceRef: ",
    "Website: "
];
/**
 * Parses the pipe-delimited body of a "Service Selection:" annotation.
 * Backward-compatible: old notes without ServiceName/ServiceRef fields return
 * undefined for those.
 */ const parseSelectionNoteBody = (body)=>{
    const parts = body.split(" | ");
    const serviceNamePart = parts.find((p)=>p.startsWith("ServiceName: "));
    const serviceRefPart = parts.find((p)=>p.startsWith("ServiceRef: "));
    const websitePart = parts.find((p)=>p.startsWith("Website: "));
    const rationaleParts = parts.filter((p)=>!SELECTION_SEGMENT_PREFIXES.some((prefix)=>p.startsWith(prefix)));
    const websiteUrls = websitePart ? websitePart.replace("Website: ", "").split(" ; ").map((u)=>u.trim()).filter(Boolean) : [];
    return {
        serviceName: serviceNamePart?.replace("ServiceName: ", "") || undefined,
        serviceRef: serviceRefPart?.replace("ServiceRef: ", "") || undefined,
        rationale: rationaleParts.join(" | ") || undefined,
        websiteUrls
    };
};
/**
 * Parses the most recent "Service Selection:" annotation from a
 * ServiceRequest's note array.
 * @agentic @benchmark/core
 */ const getLatestSelectionNote = (sr)=>{
    const selectionNotes = (sr.note ?? []).filter((n)=>n.text?.startsWith("Service Selection:"));
    if (selectionNotes.length === 0) return null;
    const latest = selectionNotes[selectionNotes.length - 1];
    const body = latest.text?.replace("Service Selection: ", "") ?? "";
    return {
        ...parseSelectionNoteBody(body),
        authorString: latest.authorString || undefined,
        time: latest.time || undefined
    };
};
/** Prefixes for decision annotations (clinical reviews, member responses, approvals). */ const DECISION_NOTE_PREFIXES = [
    {
        prefix: "Service Rejection: ",
        source: "clinical"
    },
    {
        prefix: "Service Approved: ",
        source: "clinical"
    },
    {
        prefix: "Member Response: ",
        source: "member"
    },
    {
        prefix: "Member Accepted: ",
        source: "member"
    }
];
/**
 * Parses all decision annotations from a ServiceRequest's note array.
 * @agentic @benchmark/core
 */ const getDecisionNotes = (sr)=>(sr.note ?? []).map((n)=>{
        const match = DECISION_NOTE_PREFIXES.find((p)=>n.text?.startsWith(p.prefix));
        if (!match) return null;
        const body = n.text?.replace(match.prefix, "") ?? "";
        const parts = body.split(" | ");
        const decisionPart = parts.find((p)=>p.startsWith("Decision: "));
        const rationaleParts = parts.filter((p)=>!p.startsWith("Decision: "));
        return {
            rationale: rationaleParts.join(" | ") || undefined,
            decision: decisionPart?.replace("Decision: ", "") || undefined,
            source: match.source,
            authorString: n.authorString || undefined,
            time: n.time || undefined
        };
    }).filter((n)=>n !== null);
/**
 * Returns the latest approval note for a ServiceRequest, if any.
 * @agentic @benchmark/core
 */ const getLatestApprovalNote = (sr)=>{
    const approvalNotes = getDecisionNotes(sr).filter((n)=>n.decision === "approved");
    return approvalNotes.length > 0 ? approvalNotes[approvalNotes.length - 1] : null;
};
/**
 * Returns the latest member acceptance note for a ServiceRequest, if any.
 * @agentic @benchmark/core
 */ const getLatestMemberAcceptanceNote = (sr)=>{
    const acceptanceNotes = getDecisionNotes(sr).filter((n)=>n.decision === "accepted" && n.source === "member");
    return acceptanceNotes.length > 0 ? acceptanceNotes[acceptanceNotes.length - 1] : null;
};
/**
 * Parses ALL "Service Selection:" annotations from a ServiceRequest's note array.
 * @agentic @benchmark/core
 */ const getAllSelectionNotes = (sr)=>(sr.note ?? []).filter((n)=>n.text?.startsWith("Service Selection:")).map((n)=>{
        const body = n.text?.replace("Service Selection: ", "") ?? "";
        return {
            ...parseSelectionNoteBody(body),
            authorString: n.authorString || undefined,
            time: n.time || undefined
        };
    });
/**
 * Builds a chronological, unified timeline across all ServiceRequests.
 * @agentic @benchmark/core
 */ const buildServiceRequestTimeline = (serviceRequests)=>{
    const entries = [];
    for (const sr of serviceRequests){
        const srId = sr.id ?? "unknown";
        const serviceType = getServiceTypeDisplay(sr);
        const preferences = getPreferencesText(sr);
        if (preferences) {
            entries.push({
                serviceRequestId: srId,
                serviceType,
                type: "preference",
                time: sr.authoredOn,
                authorString: undefined,
                summary: preferences,
                badgeLabel: "Member Preference",
                badgeColor: "purple"
            });
        }
        for (const sel of getAllSelectionNotes(sr)){
            entries.push({
                serviceRequestId: srId,
                serviceType,
                type: "selection",
                time: sel.time,
                authorString: sel.authorString,
                summary: sel.rationale || "Service selected",
                detail: sel.websiteUrls.length > 0 ? sel.websiteUrls.join(", ") : undefined,
                badgeLabel: "Selected",
                badgeColor: "blue",
                selectedServiceName: sel.serviceName
            });
        }
        for (const dec of getDecisionNotes(sr)){
            const isApproval = dec.decision === "approved";
            const isMember = dec.source === "member";
            let badgeLabel;
            let badgeColor;
            if (isMember) {
                badgeLabel = dec.decision === "accepted" ? "Member Accepted" : "Member Response";
                badgeColor = dec.decision === "accepted" ? "green" : "orange";
            } else if (isApproval) {
                badgeLabel = "Approved";
                badgeColor = "green";
            } else if (dec.decision === "denied") {
                badgeLabel = "Denied";
                badgeColor = "red";
            } else {
                badgeLabel = "More Info Needed";
                badgeColor = "orange";
            }
            entries.push({
                serviceRequestId: srId,
                serviceType,
                type: "decision",
                time: dec.time,
                authorString: dec.authorString,
                summary: dec.rationale || badgeLabel,
                badgeLabel,
                badgeColor
            });
        }
    }
    entries.sort((a, b)=>{
        if (!a.time && !b.time) return 0;
        if (!a.time) return -1;
        if (!b.time) return 1;
        return new Date(a.time).getTime() - new Date(b.time).getTime();
    });
    return entries;
};
/**
 * Human-readable visit/delivery mode from ServiceRequest.locationCode
 * (visit-mode codes). Prefers each coding's display, then labels from
 * `SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS`.
 * @agentic @benchmark/core
 */ const getServiceRequestVisitModeDisplay = (sr)=>{
    const codings = sr.locationCode?.[0]?.coding ?? [];
    const labels = codings.filter((c)=>Boolean(c?.code)).map((c)=>{
        const trimmed = c.display?.trim();
        if (trimmed) {
            return trimmed;
        }
        const option = SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((o)=>o.value === c.code);
        return option?.label ?? c.code;
    });
    if (labels.length === 0) {
        return "—";
    }
    return labels.join(", ");
};

export { CARE_TEAM_SNOMED_ROLES, HIGH_ACUITY_RISK_LEVELS, PRE_INTAKE_ENGAGEMENT_STATUS_CODES, SAFETY_SIGNAL_RULES, actionHasPredecessor, applyOwnerBusinessRules, areAllActivitiesCompleted, buildPatientSummaryExtension, buildScoreObservationByResponseId, buildServiceRequestTimeline, calculateCarePlanProgress, canActivateCarePlan, canCompleteCarePlan, cocmBillingSummaryToView, computeAssessmentTrends, deriveNotificationRiskSignal, deriveObservationResult, deriveQuestionnaireResult, determinePhonePriority, filterCoCMFlags, filterMinorFlags, findPrimaryPerformerParticipant, formatBillingMonthLabel, formatBillingReport, formatCareTimeActivityLabel, formatCareTimeEntryLine, formatCareTimeEntryRow, formatDateAsMMDDYY, formatPatientAddress, formatStatusReasonForDisplay, getActiveCarePlans, getActiveCarePlansByType, getActivePatientAddress, getActivePatientEmail, getActivePatientPhone, getActivePatientTelecom, getActivePhoneNumbers, getActivityStatusColor, getActivityStatusDisplay, getAllSelectionNotes, getAppointmentDisplayName, getCarePlanActivity, getCarePlanDisplayTitle, getCarePlanIdFromTask, getCarePlanSubtitle, getCarePlanType, getClinicalImpressionAuthorName, getClinicalImpressionDisplayName, getCoCMConsentDate, getCoCMConsentFromPatient, getCommunicationChannelLabel, getCommunicationDisplayName, getCommunicationSenderName, getCompletedActivitiesCount, getCompletedActivitiesCountWithTasks, getCompletedCarePlans, getConditionDisplays, getContactDetailEmail, getCurrentPatientAddresses, getDecisionNotes, getEncounterCompletionLabel, getEncounterDescription, getEncounterDisplayName, getEncounterPrimaryPerformerName, getEngagementIconName, getEngagementStatus, getFlagDisplayName, getGoalAverageScore, getGoalCategoryDisplay, getGoalCurrentScore, getGoalLastUpdateDate, getGoalLatestProgressNote, getGoalProgressHistory, getGoalStatusBadge, getHealthcareServiceDirectoryCardModel, getHealthcareServiceLogModalSubtitle, getLCSWDisplayName, getLatestApprovalNote, getLatestMemberAcceptanceNote, getLatestSelectionNote, getLibraryDocumentCategory, getLibraryTopicCodes, getLibraryTopicDisplays, getLocationDisplayName, getNextIncompleteActivity, getObservationDisplayName, getObservationPerformerName, getOrganizationDisplayName, getPCPDisplayName, getPatientBenchmarkLCSW, getPatientDisplayName, getPatientEngagementStatus, getPatientIntakeEligibility, getPatientMRN, getPatientManagingOrganization, getPatientPauseStatus, getPatientReferenceDisplay, getPatientReferringProvider, getPatientSummary, getPauseReason, getPhoneTypeLabel, getPlanDefinitionAction, getPlanDefinitionActionCodes, getPlanDefinitionByType, getPractitionerDisplayName, getPractitionerEmail, getPrePauseCarePlanStatus, getPredecessorActionCode, getPreferencesText, getPrimaryPhoneNumber, getQuestionnaireDisplayName, getQuestionnaireResponseDisplayName, getReferrerName, getRemainingPauseDays, getSelectedServiceDisplay, getServiceProviderName, getServiceRequestVisitModeDisplay, getServiceTypeDisplay, getTaskAssigneeName, getTaskCompletionAttributionDisplay, getTaskCompletionTimelineActivityName, getTaskCompletionTimelineActivityOrder, getTaskDraftFormState, getTaskDueDate, getTaskRequesterName, getTaskStatusReasonDisplay, getTaskTypeDisplay, getTotalActivitiesCount, groupObservationsByCategory, groupQuestionnaireResultsByAdaptiveSession, hasActiveCarePlanOfType, hasCompletedOfferServicesTask, hasSelectedService, isActiveAssessmentObservation, isActivityCompleted, isAiGeneratedTag, isAutoGeneratedCareTime, isCarePlanActive, isCarePlanCompleted, isCoCMCarePlan, isCoCMFlag, isDialpadCommunication, isFlagWithinActivePeriod, isGoalOverdue, isMinorFlag, isPauseFlag, isPauseFlagExpired, isPhoneCallCommunication, isSmsCommunication, mapTaskStatusToActivityStatus, mergePauseFlagPeriods, monthOfEnrollment, parseStatusReason, pauseFlagsOverlap, practitionerToContactDetail, practitionerToStakeholder, removePatientSummaryExtension, resolveCareTeamAdvocate, resolveCareTeamLCSW, resolveCareTeamPractitionerRole, resolveCareTimeActivityDetail, resolveEngagementStatusFromCarePlans, resolveNextReadyTaskActivity, resolvePatientCarePlanStatus, resolveServiceRequestDaysWaiting, resolveTaskActivityCode, resolveTaskActivityCodeByRef, resolveTaskAssignment, serializeBillingReport, shouldEngagePatientOnIntakeCompletion, shouldShowCommunicationInTimeline, stakeholderToContactDetail, updatePatientSummaryExtension };
